import React, { useState, useCallback, useRef, FC } from 'react'
import { 
  Card, 
  CardContent
} from '@/components/ui/card'
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Download, 
  AlertCircle, 
  Target, 
  ChevronDown, 
  Upload, 
  File, 
  Paperclip,
  User,
  Calendar,
  FileText,
  Clock,
  CheckSquare,
  ExternalLink,
  Save,
  Star,
  Info,
  Lock,
  ArrowRight,
  Send,
  Check,
  X
} from 'lucide-react'
import { EnhancedEvidenceEntry } from '@/types/evidence'
import { performanceService } from '@/services/api/performanceService'
import toast from 'react-hot-toast'

// Infer measurement mode from existing KPI data (for backward compatibility)
const inferMeasurementMode = (kpi: { unit?: string; measurement_mode?: string }): 'numeric' | 'percentage' | 'rating' | 'binary' => {
  if (kpi.measurement_mode) return kpi.measurement_mode as 'numeric' | 'percentage' | 'rating' | 'binary'
  
  const unit = (kpi.unit || '').toLowerCase().trim()
  
  if (unit === '%' || unit === 'percent' || unit === 'percentage') {
    return 'percentage'
  }
  if (unit === 'status' || unit === 'rag' || unit === 'rating') {
    return 'rating'
  }
  if (unit === 'achieved' || unit === 'yes/no' || unit === 'complete') {
    return 'binary'
  }
  
  return 'numeric'
}

// Infer measurement mode from unit string (for history display)
const inferModeFromUnit = (unit?: string): 'numeric' | 'percentage' | 'rating' | 'binary' => {
  const unitLower = (unit || '').toLowerCase().trim()
  if (unitLower === 'status' || unitLower === 'rag' || unitLower === 'rating') return 'rating'
  if (unitLower === 'achieved' || unitLower === 'yes/no' || unitLower === 'complete') return 'binary'
  if (unitLower === '%' || unitLower === 'percent' || unitLower === 'percentage') return 'percentage'
  return 'numeric'
}

// Format a measurement value based on mode for display
const formatMeasurementValue = (value: number | null | undefined, mode: 'numeric' | 'percentage' | 'rating' | 'binary', unit?: string): string => {
  if (value === null || value === undefined) return '--'
  
  if (mode === 'rating') {
    if (value >= 100) return 'On Track'
    if (value >= 50) return 'Mostly on Track'
    if (value > 0) return 'Not on Track'
    return 'Not Rated'
  }
  
  if (mode === 'binary') {
    return value >= 100 ? 'Achieved' : 'Not Achieved'
  }
  
  // Numeric/percentage - show value with unit
  return `${value}${unit ? ` ${unit}` : ''}`
}

// Unified entry-date format used in BOTH Simple and Detailed views:
// "3 May 2026" — sentence case, no slashes, no leading zero. Keeps the two
// densities reading as one surface (rework task #10).
const formatEntryDate = (value: string | number | Date | null | undefined): string => {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

// Get color class for rating/binary values.
//
// `detailed` gates the traffic-light (RAG) palette: it is ONLY applied in
// Detailed view. In Simple view (default, and when `detailed` is omitted) we
// return the calm stone/neutral palette so the safe-space surface never shows
// red/amber/green. Defaulting to `false` keeps the function backward-compatible
// for any caller that doesn't opt into RAG.
const getMeasurementColorClass = (
  value: number | null | undefined,
  mode: 'numeric' | 'percentage' | 'rating' | 'binary',
  type: 'text' | 'bg' = 'text',
  detailed = false,
): string => {
  if (value === null || value === undefined) return type === 'text' ? 'text-stone-500' : 'bg-stone-100'

  // Simple view: calm neutral surface regardless of value/mode.
  if (!detailed) return type === 'text' ? 'text-stone-700' : 'bg-stone-100'

  if (mode === 'rating') {
    if (value >= 100) return type === 'text' ? 'text-green-700' : 'bg-green-100'
    if (value >= 50) return type === 'text' ? 'text-amber-700' : 'bg-amber-100'
    if (value > 0) return type === 'text' ? 'text-red-700' : 'bg-red-100'
    return type === 'text' ? 'text-gray-500' : 'bg-gray-100'
  }

  if (mode === 'binary') {
    return value >= 100
      ? (type === 'text' ? 'text-green-700' : 'bg-green-100')
      : (type === 'text' ? 'text-gray-500' : 'bg-gray-100')
  }

  return type === 'text' ? 'text-gray-700' : 'bg-gray-100'
}

// Infer measurement direction from existing KPI data
const inferMeasurementDirection = (kpi: { unit?: string; name?: string; measurement_direction?: string }): 'higher_is_better' | 'lower_is_better' => {
  if (kpi.measurement_direction) return kpi.measurement_direction as 'higher_is_better' | 'lower_is_better'
  
  const unit = (kpi.unit || '').toLowerCase()
  const name = (kpi.name || '').toLowerCase()
  
  if (unit.includes('day') || unit.includes('hour') || unit.includes('minute') ||
      name.includes('turnaround') || name.includes('response time') || 
      name.includes('waiting') || name.includes('defect') || name.includes('error')) {
    return 'lower_is_better'
  }
  
  return 'higher_is_better'
}

interface KPIOption {
  id: string
  name: string
  /**
   * The KPA this KPI belongs to. Used to scope the KPI Select in the Add
   * Entry dialog: when the user picks a KPA, the KPI list narrows to that
   * KPA's KPIs only. Optional for back-compat with callers that haven't
   * been updated to populate it yet — those KPIs still surface (unscoped).
   */
  kpaId?: string
  objectiveTitle?: string
  current_value?: number
  target_value?: number
  unit?: string
  measurement_history?: string | null  // KPI-level centralized measurement history (JSON)
  // Enterprise Measurement Configuration
  measurement_direction?: 'higher_is_better' | 'lower_is_better'
  measurement_mode?: 'numeric' | 'percentage' | 'rating' | 'binary'
  rating_scale?: 'rag' | 'three_point' | 'five_point' | 'custom'
  rating_options?: Array<{ label: string; value: number; color?: string }>
  min_value?: number | null
  max_value?: number | null
}

interface EvidencePortfolioProps {
  evidence: EnhancedEvidenceEntry[]
  userRole: 'employee' | 'manager'
  currentUserId?: string // The logged-in user's ID (for permission checks)
  targetUserId?: string // For manager view - which employee's evidence
  kpaId?: string // Optional KPA context for evidence
  /**
   * KPA picker options for the Add Entry dialog when the parent doesn't
   * pre-bind a specific KPA via `kpaId`. Drives the "Link to a KPA"
   * Select on the top-level Portfolio surface — leaving it blank stores
   * the entry as a **General** entry (kpaId=null), which is the "General
   * category" requested by HR/people-leader feedback.
   *
   * Per-KPA surfaces (e.g. `MemberKPAPanel`) bind kpaId via the prop, so
   * the picker doesn't show there; a per-KPA observations surface passes its
   * one KPA here only so each observation can show its KPA tag.
   *
   * `numericId` is the KPA's numeric id, which saved entries carry: options
   * are keyed by the KPA's slug, so tags and the area filter match either.
   */
  kpaOptions?: Array<{ id: string; name: string; numericId?: string | number | null }>
  availableKPIs?: KPIOption[] // Available KPIs to link evidence to
  preSelectedKPIId?: string // Pre-select a specific KPI when opening
  hideUploadButton?: boolean // Hide the Upload Document button in header (upload still available in evidence form)
  readOnly?: boolean // Read-only mode - hides all edit/delete/add buttons
  reviewPeriod?: 'MIDYEAR' | 'YEAREND' // Filter measurement history by review period
  /**
   * Skip the entry-LIST display filter by `reviewPeriod` while still using
   * `reviewPeriod` for everything else (measurement attribution, period-value
   * display). Used by the People Leader's top-level Impact Portfolio tab, which
   * pre-filters by RELEASE, not capture period — without this, an entry released
   * for this period but captured in the other period would be wrongly hidden.
   */
  skipPeriodDisplayFilter?: boolean
  /**
   * 'simple' (default) is the safe-space view: hides classification, validation,
   * source badges, KPI deltas, and other metric-anxiety UI. 'detailed' restores
   * the full set of fields for power users (managers / HR).
   */
  viewMode?: 'simple' | 'detailed'
  /**
   * If true, manager view shows an informational "only the employee can add"
   * Alert in place of the (hidden) Add button. Defaults to false to preserve
   * existing behavior for surfaces that don't need the affordance.
   */
  showManagerCannotAddNotice?: boolean
  /**
   * When true, render this surface as **People Leader Observations** instead
   * of the team member's Impact Portfolio:
   *   - canAddEntry inverts: the PL (currentUserId !== targetUserId) can add,
   *     the team member cannot.
   *   - Only `source='manager'` entries authored by the current PL are shown
   *     (server-side confidentiality already enforces this on read, but we
   *     belt-and-braces filter client-side too in case the parent passes a
   *     mixed list).
   *   - Header labelling switches to "Leader Observations".
   *   - The manager-cannot-add notice is suppressed (it's the opposite case).
   *
   * Note: PL Observations are intentionally private to the PL. They are NOT
   * visible to the team member — that's enforced server-side by the read
   * filter on DRAFT/confidential evidence.
   */
  observationsMode?: boolean
  /**
   * Optional employee display name used in the manager-cannot-add notice.
   */
  ownerDisplayName?: string
  checkEvidenceEditable?: (evidence: EnhancedEvidenceEntry) => { canEdit: boolean; reason?: string } // Check if evidence is from a locked period
  onAddEvidence: (evidence: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => Promise<EnhancedEvidenceEntry | undefined> | void
  onUpdateEvidence?: (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => void
  onDeleteEvidence: (evidenceId: string) => void
  onUploadDocument: (file: File, evidenceData: Partial<EnhancedEvidenceEntry>) => Promise<void>
  onClassifyEvidence?: (evidenceId: string, classification: EnhancedEvidenceEntry['classification']) => void // Manager only
  onUpdateEvidenceLocal?: (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => void // Local state update only
  /**
   * Called when the user invokes "Continue at Year-End" on a fully-locked
   * Mid-Year entry. Resolves to the new follow-up evidence (or surfaces a
   * KPI-retired warning). Wiring is supplied by the parent so it can pass
   * the right userId and refresh the list afterwards.
   */
  onCarryForwardEvidence?: (evidenceId: string) => Promise<{
    evidence: EnhancedEvidenceEntry
    kpi_carry_forward_warning: 'kpi_no_longer_active' | null
    idempotent: boolean
  } | undefined>

  // -------- A2: per-entry release model --------
  //
  // The parent passes the set of pending check-in sessions the team member
  // can release entries to. Empty / undefined → no Release UI rendered.
  // Binary release: a single Shared / Private toggle per entry, derived from
  // `evidenceItem.isReleased` + `lockState`. The parent owns the actual service
  // call so it can patch local state with the returned evidence row.
  onReleaseEvidence?: (evidenceId: string) => Promise<EnhancedEvidenceEntry | undefined>
  onWithdrawEvidence?: (evidenceId: string) => Promise<EnhancedEvidenceEntry | undefined>

  onRefreshEvidence?: () => Promise<void> // Refresh evidence list from server
}

/**
 * Renders the body of a STAKEHOLDER_FEEDBACK evidence entry.
 *
 * The backend stores the feedback content as a single text blob (built by
 * `_generate_feedback_summary` / `_feedback_response_body`): a "Received N
 * feedback response(s)." header, optional "Response K" / "Overall rating: X/5"
 * markers, then `Question?\nAnswer` blocks separated by blank lines. This
 * parses that blob back into structured segments so questions and answers can
 * be styled distinctly, and collapses long bodies (multiple responses) behind
 * a "Show more" toggle so the card never balloons.
 *
 * Anything that doesn't match the known shape falls through to a plain
 * paragraph, so the component degrades gracefully on unexpected content.
 */
type FeedbackSegment =
  | { kind: 'header'; text: string }
  | { kind: 'response'; text: string }
  | { kind: 'rating'; text: string }
  | { kind: 'qa'; question: string; answer: string }
  | { kind: 'text'; text: string }

function parseStakeholderFeedback(description: string): FeedbackSegment[] {
  return (description || '')
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean)
    .map((block): FeedbackSegment => {
      if (/^Received\s+\d+\s+feedback\s+response/i.test(block)) return { kind: 'header', text: block }
      if (/^Response\s+\d+$/i.test(block)) return { kind: 'response', text: block }
      if (/^Overall rating:/i.test(block)) return { kind: 'rating', text: block }
      const lines = block.split('\n')
      if (lines.length >= 2 && /\?\s*$/.test(lines[0].trim())) {
        return { kind: 'qa', question: lines[0].trim(), answer: lines.slice(1).join('\n').trim() }
      }
      return { kind: 'text', text: block }
    })
}

const StakeholderFeedbackBody: FC<{ description: string; viewMode: 'simple' | 'detailed' }> = ({
  description,
  viewMode,
}) => {
  const [expanded, setExpanded] = useState(false)
  const isSimple = viewMode === 'simple'
  const segments = parseStakeholderFeedback(description)
  const header = segments.find((s) => s.kind === 'header') as Extract<FeedbackSegment, { kind: 'header' }> | undefined
  const content = segments.filter((s) => s.kind !== 'header')

  // Collapse once the body gets long — a single response (≈3 Q/A, optional
  // rating) stays fully visible; multiple responses fold away.
  const COLLAPSE_AFTER = 4
  const collapsible = content.length > COLLAPSE_AFTER
  const visible = collapsible && !expanded ? content.slice(0, COLLAPSE_AFTER) : content
  const hiddenCount = content.length - visible.length

  const questionCls = isSimple
    ? 'not-italic font-semibold text-quarterly-ink text-[15px] leading-snug'
    : 'font-semibold text-gray-900 text-sm'
  const answerCls = isSimple
    ? 'font-editorial italic text-quarterly-ink-muted text-[16px] leading-[1.55] whitespace-pre-line'
    : 'text-gray-600 text-sm whitespace-pre-line'

  return (
    <div className="mb-4 max-w-[640px]">
      {header && (
        <p
          className={
            isSimple
              ? 'not-italic text-[11px] tracking-[0.18em] uppercase text-quarterly-ink-muted/70 mb-4'
              : 'text-xs uppercase tracking-wide text-gray-400 mb-3'
          }
        >
          {header.text.replace(/\.$/, '')}
        </p>
      )}

      <div className="space-y-4">
        {visible.map((seg, i) => {
          if (seg.kind === 'response') {
            return (
              <p
                key={i}
                className={
                  isSimple
                    ? 'not-italic text-[11px] tracking-[0.18em] uppercase text-quarterly-tobacco font-medium pt-1'
                    : 'text-[11px] uppercase tracking-wide text-heritage-blue font-semibold pt-1'
                }
              >
                {seg.text}
              </p>
            )
          }
          if (seg.kind === 'rating') {
            return (
              <div
                key={i}
                className={
                  isSimple
                    ? 'inline-flex items-center gap-1.5 not-italic text-[13px] text-quarterly-ink'
                    : 'inline-flex items-center gap-1.5 text-sm text-gray-700'
                }
              >
                <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />
                {seg.text}
              </div>
            )
          }
          if (seg.kind === 'qa') {
            return (
              <div
                key={i}
                className={
                  isSimple
                    ? 'border-l-2 border-quarterly-rule pl-4'
                    : 'border-l-2 border-gray-200 pl-3'
                }
              >
                <p className={questionCls}>{seg.question}</p>
                <p className={cn(answerCls, 'mt-1')}>{seg.answer}</p>
              </div>
            )
          }
          return (
            <p key={i} className={answerCls}>
              {seg.text}
            </p>
          )
        })}
      </div>

      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={
            isSimple
              ? 'not-italic mt-4 inline-flex items-center gap-1 text-[12px] tracking-[0.12em] uppercase text-quarterly-tobacco hover:text-quarterly-ink transition-colors'
              : 'mt-3 inline-flex items-center gap-1 text-sm text-heritage-blue hover:underline'
          }
        >
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
          <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
        </button>
      )}
    </div>
  )
}

// Why an entry didn't save, in words: the server's reason when it gives one,
// a refusal raised by the page as is, otherwise a plain "not saved".
const saveErrorText = (e: unknown): string => {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return `The entry was not saved: ${detail}`
  const fromServer = !!(e as { response?: unknown; isAxiosError?: boolean })?.response
    || !!(e as { isAxiosError?: boolean })?.isAxiosError
  if (e instanceof Error && !fromServer && e.message) return e.message
  return 'The entry was not saved. Please try again.'
}

export default function EvidencePortfolio({
  evidence,
  userRole,
  currentUserId,
  targetUserId,
  kpaId,
  kpaOptions,
  availableKPIs = [],
  preSelectedKPIId,
  hideUploadButton = false,
  readOnly = false,
  reviewPeriod,
  skipPeriodDisplayFilter = false,
  viewMode = 'simple',
  showManagerCannotAddNotice = false,
  observationsMode = false,
  ownerDisplayName,
  checkEvidenceEditable,
  onAddEvidence,
  onUpdateEvidence,
  onDeleteEvidence,
  onUploadDocument,
  onClassifyEvidence,
  onUpdateEvidenceLocal,
  onCarryForwardEvidence,
  onReleaseEvidence,
  onWithdrawEvidence,
  onRefreshEvidence
}: EvidencePortfolioProps) {
  // Acknowledge unused props to suppress lint warnings
  void onUpdateEvidenceLocal

  // RAG gate (DECISION: traffic-light only in Detailed view). This closure
  // forwards the component's current `viewMode` into `getMeasurementColorClass`
  // so every call site picks up the calm stone palette in Simple view and the
  // red/amber/green palette in Detailed view, without each call repeating the
  // `viewMode === 'detailed'` check.
  const measurementColor = (
    value: number | null | undefined,
    mode: 'numeric' | 'percentage' | 'rating' | 'binary',
    type: 'text' | 'bg' = 'text',
  ): string => getMeasurementColorClass(value, mode, type, viewMode === 'detailed')


  // Helper to format user display name
  const formatUserName = (uid: string): string => {
    // TODO: Look up actual user name from identity service
    // For now, return the ID as-is (personnel numbers are numeric)
    return uid || 'Unknown';
  };
  
  // Measurement History Component
  const MeasurementHistory: FC<{history: string | any[] | null; unit?: string; reviewPeriod?: 'MIDYEAR' | 'YEAREND'}> = ({ history, unit, reviewPeriod: filterPeriod }) => {
    const [showHistory, setShowHistory] = useState(false);
    
    if (!history) return null;
    
    // Infer mode from unit for display formatting
    const mode = inferModeFromUnit(unit);
    const isRatingOrBinary = mode === 'rating' || mode === 'binary';
    
    try {
      // Handle both JSON string and already-parsed array
      let historyData = typeof history === 'string' ? JSON.parse(history) : history;
      if (!Array.isArray(historyData) || historyData.length === 0) return null;
      
      // Filter by review period if specified
      if (filterPeriod) {
        historyData = historyData.filter((entry: any) => 
          !entry.review_period || entry.review_period === filterPeriod
        );
        if (historyData.length === 0) return null;
      }
      
      return (
        <div className="mt-3 pt-3 border-t border-emerald-200/50">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between text-xs font-medium text-emerald-700 hover:text-emerald-900 transition-colors group"
          >
            <span className="flex items-center gap-2">
              <span className="group-hover:scale-110 transition-transform">{showHistory ? '▼' : '▶'}</span>
              <span>Measurement History</span>
            </span>
            <span className="text-emerald-600/70">{historyData.length} {historyData.length === 1 ? 'entry' : 'entries'}</span>
          </button>
          {showHistory && (
            <div className="mt-2 space-y-1.5 max-h-36 overflow-y-auto">
              {historyData.slice().reverse().map((entry: any, idx: number, arr: any[]) => {
                // Baseline = the earliest entry shown (oldest in this filtered
                // view, rendered last) OR an entry the backend marked with
                // previous=null. A baseline has no prior data point, so we render
                // it as "—" → value with a "New" badge rather than a misleading
                // "X → X" / 0.0 delta against the seeded value.
                const hasPrev = entry.previous !== null && entry.previous !== undefined;
                const isFirst = !hasPrev || idx === arr.length - 1;
                const change = isFirst ? null : entry.value - entry.previous;
                const isIncrease = change !== null && change > 0;
                const prevDisplay = isFirst ? '—' : formatMeasurementValue(entry.previous, mode, unit);
                const newDisplay = formatMeasurementValue(entry.value, mode, unit);
                
                return (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white/50 rounded-lg border border-emerald-100/50 hover:border-emerald-200 hover:shadow-sm transition-all">
                    <div className="flex items-center gap-3">
                      {/* Icon - for rating/binary show status icon instead */}
                      {isRatingOrBinary ? (
                        <div className={`flex items-center justify-center w-7 h-7 rounded-md ${measurementColor(entry.value, mode, 'bg')}`}>
                          <span className={`text-sm font-bold leading-none ${measurementColor(entry.value, mode, 'text')}`}>
                            {mode === 'binary' ? (entry.value >= 100 ? '✓' : '○') : 
                             mode === 'rating' ? (entry.value >= 100 ? '●' : entry.value >= 50 ? '◐' : '○') : ''}
                          </span>
                        </div>
                      ) : (
                        <div className={`flex items-center justify-center w-7 h-7 rounded-md ${isFirst ? 'bg-blue-100' : isIncrease ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                          <span className={`text-sm font-bold leading-none ${isFirst ? 'text-blue-600' : isIncrease ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {isFirst ? '•' : isIncrease ? '↗' : '↘'}
                          </span>
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="flex flex-col gap-1">
                        <div className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                          {isRatingOrBinary ? (
                            <>
                              <span className={measurementColor(entry.previous, mode, 'text')}>{prevDisplay}</span>
                              <span className="text-gray-400">→</span>
                              <span className={measurementColor(entry.value, mode, 'text')}>{newDisplay}</span>
                            </>
                          ) : (
                            <>{prevDisplay} → {newDisplay}</>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-gray-500">
                          <span className="font-medium">
                            {(() => {
                              // Add 'Z' if not present to indicate UTC
                              const timestamp = entry.timestamp.endsWith('Z') ? entry.timestamp : entry.timestamp + 'Z';
                              return new Date(timestamp).toLocaleString('en-ZA', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                            })()}
                          </span>
                          {entry.submitted_by && (
                            <>
                              <span className="text-gray-400">•</span>
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {formatUserName(entry.submitted_by)}
                              </span>
                            </>
                          )}
                          {entry.evidence_id && evidence.find((e: any) => e.id === entry.evidence_id) && (
                            <>
                              <span className="text-gray-400">•</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const evidenceEl = document.getElementById(`evidence-${entry.evidence_id}`);
                                  evidenceEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  evidenceEl?.classList.add('ring-2', 'ring-blue-400');
                                  setTimeout(() => evidenceEl?.classList.remove('ring-2', 'ring-blue-400'), 2000);
                                }}
                                className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                              >
                                {evidence.find((e: any) => e.id === entry.evidence_id)?.title || 'View entry'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Change badge - hide for rating/binary, show status instead */}
                    {isRatingOrBinary ? (
                      <div className={`px-2.5 py-1 rounded-md text-xs font-bold ${measurementColor(entry.value, mode, 'bg')} ${measurementColor(entry.value, mode, 'text')}`}>
                        {newDisplay}
                      </div>
                    ) : isFirst ? (
                      <div className="px-2.5 py-1 rounded-md text-xs font-bold bg-blue-50 text-blue-700">
                        New
                      </div>
                    ) : (
                      <div className={`px-2.5 py-1 rounded-md text-xs font-bold ${isIncrease ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {isIncrease ? '+' : ''}{(change ?? 0).toFixed(1)}{unit ? ` ${unit}` : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    } catch (e) {
      return null;
    }
  };
  
  const [showEvidenceForm, setShowEvidenceForm] = useState(false)
  const [showDocumentUpload, setShowDocumentUpload] = useState(false)
  const [editingEvidence, setEditingEvidence] = useState<EnhancedEvidenceEntry | null>(null)
  // Top-level Impact Portfolio filters (only rendered on the mixed view where
  // kpaId is undefined — both the team member's "my Impact Portfolio" and the
  // People Leader's portfolio tab). Per-KPA panels don't show these.
  const [kpaFilter, setKpaFilter] = useState<string>('all') // 'all' | kpaId | 'general'
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [portfolioSearch, setPortfolioSearch] = useState<string>('')
  // The KPA option an entry belongs to. Options are keyed by the KPA's slug but
  // a saved entry carries its numeric id, so match either: matching the slug
  // alone left a KPA-linked observation without its KPA tag, and the area
  // filter found nothing for a KPA (HR UAT list #2).
  const kpaOptionFor = (entryKpaId?: string | null) =>
    entryKpaId == null || entryKpaId === ''
      ? undefined
      : kpaOptions?.find((o) =>
          String(o.id) === String(entryKpaId) || (o.numericId != null && String(o.numericId) === String(entryKpaId)))
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  // Carry-forward (Mid-Year → Year-End thread continuation) — see the
  // backend POST /evidence/{id}/carry-forward endpoint. The dialog needs
  // the source row to render the KPI-retired warning at confirmation time.
  const [carryForwardSource, setCarryForwardSource] = useState<EnhancedEvidenceEntry | null>(null)
  const [carryingForward, setCarryingForward] = useState(false)
  const [evidenceToDelete, setEvidenceToDelete] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [measurementValue, setMeasurementValue] = useState<string>('')
  // Which add path the user chose from the header: 'entry' (general impact &
  // growth narrative) or 'measurement' (record a KPI number). Drives the
  // initial form type and un-gates the measurement form before a KPI is picked
  // so the user can choose the KPI in-dialog (discoverable measurement entry
  // point). Always reset to 'entry' by resetForm.
  const [addMode, setAddMode] = useState<'entry' | 'measurement'>('entry')
  const [showAdditionalFields, setShowAdditionalFields] = useState(false)
  const [uploadingAttachment, setUploadingAttachment] = useState<string | null>(null)
  // State for attachment delete confirmation
  const [attachmentToDelete, setAttachmentToDelete] = useState<{id: string, evidenceId: string, name: string} | null>(null)
  // A fresh form for this surface. Opening and resetting both use it, so a
  // surface bound to one KPI keeps that KPI after a save or cancel: the reset
  // used to drop it, which hid New Value on the next Add Measurement.
  const blankEvidenceForm = (mode: 'entry' | 'measurement' = 'entry'): Partial<EnhancedEvidenceEntry> => ({
    type: observationsMode ? 'achievement' : (mode === 'measurement' || preSelectedKPIId ? 'measurement' : 'achievement'),
    classification: 'full',
    source: userRole === 'manager' ? 'manager' : 'employee',
    title: '',
    description: '',
    impact: '',
    evidence: '',
    kpiId: preSelectedKPIId,
    reviewPeriod: reviewPeriod,  // Set the review period from prop
    appliesToBothPeriods: false  // Phase 1: opt-in dual-period flag
  })
  const [evidenceForm, setEvidenceForm] = useState<Partial<EnhancedEvidenceEntry>>(() => blankEvidenceForm(addMode))

  const getClassificationColor = (classification: EnhancedEvidenceEntry['classification']) => {
    switch (classification) {
      case 'exceptional': return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'full': return 'bg-green-100 text-green-800 border-green-200'
      case 'not-yet-full': return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'weak': return 'bg-red-100 text-red-800 border-red-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getClassificationIcon = (classification: EnhancedEvidenceEntry['classification']) => {
    switch (classification) {
      case 'exceptional': return <Star className="h-4 w-4" />
      case 'full': return <CheckSquare className="h-4 w-4" />
      case 'not-yet-full': return <Clock className="h-4 w-4" />
      case 'weak': return <AlertCircle className="h-4 w-4" />
      default: return <FileText className="h-4 w-4" />
    }
  }

  const addEvidenceOnce = useCallback(async () => {
    if (!evidenceForm.title || !evidenceForm.description) return

    // INVARIANT: source reflects ownership at submit time, not stale form state.
    // If the current user (currentUserId) is the profile owner (targetUserId),
    // it's an employee submission on their own profile. Otherwise it's a manager
    // submission on someone else's profile. This is robust against stale userRole
    // props and useState lazy-init issues.
    const isSelfSubmission = currentUserId && targetUserId && currentUserId === targetUserId
    const computedSource: 'employee' | 'manager' = isSelfSubmission ? 'employee' : 'manager'

    // For document type, handle file upload
    if (evidenceForm.type === 'document' && selectedFile) {
      await onUploadDocument(selectedFile, {
        ...evidenceForm,
        source: computedSource,
        // Parent prop wins when it binds a KPA. When undefined (top-level
        // Portfolio), use the form's selected KPA — null/empty → General.
        kpaId: kpaId !== undefined ? kpaId : (evidenceForm.kpaId || undefined),
        version: 1,
        validationStatus: userRole === 'manager' ? 'validated' : 'pending',
        reviewPeriod: reviewPeriod  // Explicitly set review period
      })
      resetForm(true) // Close the dialog after a successful document upload
      setSelectedFile(null)
      return
    }

    // For KPI measurements, include the value and format the evidence field
    const kpi = evidenceForm.kpiId ? availableKPIs.find(k => k.id === evidenceForm.kpiId) : null
    const formattedEvidence = evidenceForm.kpiId && measurementValue && kpi
      ? `Measurement: ${measurementValue}${kpi.unit ? ` ${kpi.unit}` : ''}`
      : evidenceForm.evidence
    
    // Get the current value from KPI measurement history (most recent measurement)
    let currentKPIValue = kpi?.current_value
    if (kpi?.measurement_history) {
      try {
        const history = typeof kpi.measurement_history === 'string' 
          ? JSON.parse(kpi.measurement_history) 
          : kpi.measurement_history
        
        if (Array.isArray(history) && history.length > 0) {
          // Get the most recent measurement value
          const sortedHistory = [...history].sort((a: any, b: any) => 
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          )
          currentKPIValue = sortedHistory[0].value
          if (import.meta.env.DEV) console.log('📊 Create: Using most recent measurement as previous:', currentKPIValue)
        }
      } catch (e) {
        console.warn('Failed to parse measurement history for create:', e)
      }
    }
    
    const newEvidence = {
      ...evidenceForm,
      // Override source from form state — see invariant comment above.
      source: computedSource,
      // Parent prop wins when it binds a KPA (per-KPA surfaces). When
      // undefined (top-level Portfolio), let the form's selected KPA
      // through; null/empty → General entry.
      kpaId: kpaId !== undefined ? kpaId : (evidenceForm.kpaId || undefined),
      date: new Date().toISOString(),
      version: 1,
      validationStatus: userRole === 'manager' ? 'validated' : 'pending',
      evidence: formattedEvidence, // Store formatted measurement in evidence field
      // `visibility` is no longer sent — per-entry visibility is derived
      // server-side from source + measurement status + active releases.
      // Explicitly set review period from prop (overrides form value if different)
      reviewPeriod: reviewPeriod,
      // Include measurement value and previous value for progression tracking
      ...(evidenceForm.kpiId && measurementValue ? {
        measurementValue: parseFloat(measurementValue),
        previousValue: currentKPIValue // Use most recent measurement from history
      } : {})
    } as Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>

    let result: unknown
    try {
      result = await onAddEvidence(newEvidence)
    } catch (e) {
      // Not saved: keep the form open with what was typed, and say why.
      toast.error(saveErrorText(e))
      return
    }
    const createdEvidence = result as EnhancedEvidenceEntry | undefined
    
    // If there's a selected file and we got the evidence ID, upload it as an attachment
    if (selectedFile && createdEvidence && createdEvidence.id) {
      try {
        await performanceService.uploadAttachment(createdEvidence.id, selectedFile)
        if (import.meta.env.DEV) console.log('📎 Attachment uploaded for evidence:', createdEvidence.id)
      } catch (attachmentError) {
        console.error('Failed to upload attachment:', attachmentError)
        // Don't fail the whole operation - evidence was created
      }
      setSelectedFile(null)
    }
    
    // Refresh evidence and KPA data after add to show updated measurements
    if (onRefreshEvidence) {
      await onRefreshEvidence()
    }
    // Always close the dialog on a successful add (per feedback). Previously
    // the general portfolio view kept it open for rapid multi-add, but that
    // left users unsure the entry had saved.
    resetForm(true)
  }, [evidenceForm, selectedFile, measurementValue, targetUserId, kpaId, userRole, availableKPIs, reviewPeriod, preSelectedKPIId, onAddEvidence, onRefreshEvidence])

  // One save at a time: a second click while the first is still saving sent
  // the entry again (HR UAT list #1). The ref answers within one burst of
  // clicks; the state disables the button until the save is done.
  const savingEntryRef = useRef(false)
  const [savingEntry, setSavingEntry] = useState(false)
  const handleAddEvidence = async () => {
    if (savingEntryRef.current) return
    savingEntryRef.current = true
    setSavingEntry(true)
    try {
      await addEvidenceOnce()
    } finally {
      savingEntryRef.current = false
      setSavingEntry(false)
    }
  }

  const handleUpdateEvidence = useCallback(async () => {
    if (import.meta.env.DEV) console.log('handleUpdateEvidence called', { 
      editingEvidence: !!editingEvidence, 
      title: evidenceForm.title, 
      description: evidenceForm.description,
      hasHandler: !!onUpdateEvidence 
    })
    
    if (!editingEvidence || !evidenceForm.title || !evidenceForm.description) {
      console.warn('Update validation failed', { editingEvidence, evidenceForm })
      return
    }

    // For KPI measurements on update, capture the CURRENT KPI value as previous
    const kpi = evidenceForm.kpiId ? availableKPIs.find(k => k.id === evidenceForm.kpiId) : null
    const newMeasurement = measurementValue ? parseFloat(measurementValue) : null
    
    // Get the current value from KPI measurement history (most recent measurement)
    let currentKPIValue = kpi?.current_value
    if (kpi?.measurement_history) {
      try {
        const history = typeof kpi.measurement_history === 'string' 
          ? JSON.parse(kpi.measurement_history) 
          : kpi.measurement_history
        
        if (Array.isArray(history) && history.length > 0) {
          // Get the most recent measurement value
          const sortedHistory = [...history].sort((a: any, b: any) => 
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          )
          currentKPIValue = sortedHistory[0].value
          if (import.meta.env.DEV) console.log('📊 Update: Using most recent measurement as previous:', currentKPIValue)
        }
      } catch (e) {
        console.warn('Failed to parse measurement history for update:', e)
      }
    }

    // Phase 2: when the user edits a measurement value, target the
    // period-specific column matching the period currently being VIEWED.
    // The legacy `measurementValue` is also sent so older code paths see a
    // consistent "current pointer". The OTHER period's column is left
    // untouched — that's the whole point: editing the year-end value on a
    // multi-period entry must not rewrite the mid-year value.
    const isYearEndView = reviewPeriod === 'YEAREND'
    const periodSpecificMeasurement = (evidenceForm.kpiId && newMeasurement)
      ? (isYearEndView
          ? { yearendMeasurementValue: newMeasurement }
          : { midyearMeasurementValue: newMeasurement })
      : {}

    const updateData = {
      ...evidenceForm,
      version: editingEvidence.version + 1,
      previousVersions: [...(editingEvidence.previousVersions || []), editingEvidence.id],
      // `visibility` is no longer sent — per-entry visibility is derived
      // server-side from source + measurement status + active releases.
      // Update measurement and track previous value (use CURRENT KPI value, not old evidence value)
      ...(evidenceForm.kpiId && newMeasurement ? {
        measurementValue: newMeasurement,
        previousValue: currentKPIValue
      } : {}),
      ...periodSpecificMeasurement,
    }

    if (import.meta.env.DEV) console.log('Calling onUpdateEvidence with:', editingEvidence.id, updateData)
    
    if (onUpdateEvidence) {
      await onUpdateEvidence(editingEvidence.id, updateData)
      // Refresh evidence and KPA data after update
      if (onRefreshEvidence) {
        await onRefreshEvidence()
      }
    } else {
      console.error('onUpdateEvidence handler is not provided!')
    }
    resetForm(true) // Close form after update
  }, [editingEvidence, evidenceForm, measurementValue, availableKPIs, onUpdateEvidence, onRefreshEvidence, reviewPeriod])

  const handleDeleteClick = useCallback((evidenceId: string) => {
    setEvidenceToDelete(evidenceId)
    setDeleteConfirmOpen(true)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (evidenceToDelete) {
      await onDeleteEvidence(evidenceToDelete)
      setEvidenceToDelete(null)
      // Refresh after delete so KPI progress reflects the rolled-back measurement
      // (the backend rolls back EmployeeKPIProgress on evidence delete)
      if (onRefreshEvidence) {
        await onRefreshEvidence()
      }
    }
  }, [evidenceToDelete, onDeleteEvidence, onRefreshEvidence])

  const handleConfirmCarryForward = useCallback(async () => {
    if (!carryForwardSource || !onCarryForwardEvidence) return
    setCarryingForward(true)
    try {
      const result = await onCarryForwardEvidence(carryForwardSource.id)
      if (result?.idempotent) {
        toast.success('Year-End follow-up already exists — opening it.')
      } else if (result?.kpi_carry_forward_warning === 'kpi_no_longer_active') {
        toast(
          'Year-End follow-up created. The original KPI is no longer in your role, so it was carried forward as general impact portfolio.',
          { duration: 6000 }
        )
      } else {
        toast.success('Continued at Year-End — the new entry is editable.')
      }
      setCarryForwardSource(null)
      if (onRefreshEvidence) await onRefreshEvidence()
    } catch (err: any) {
      console.error('Carry-forward failed:', err)
      toast.error(err?.message || 'Could not continue at Year-End. Please try again.')
    } finally {
      setCarryingForward(false)
    }
  }, [carryForwardSource, onCarryForwardEvidence, onRefreshEvidence])

  // Validate type-specific required fields
  const isFormValid = useCallback(() => {
    // Basic required fields
    if (!evidenceForm.title || !evidenceForm.description) return false

    // KPI measurement requires a value
    if (evidenceForm.kpiId && !measurementValue) return false

    // A measurement add path requires a KPI to be linked — block submission
    // until one is picked so a "Record Measurement" entry can't be saved as a
    // plain narrative with no KPI behind it.
    if (addMode === 'measurement' && !evidenceForm.kpiId) return false

    // Type-specific validations
    switch (evidenceForm.type) {
      case 'document':
        return !!selectedFile || !!editingEvidence // File required for new, optional for edit
      case 'link':
        return !!evidenceForm.externalLink?.url
      default:
        return true
    }
  }, [evidenceForm, selectedFile, editingEvidence, measurementValue, addMode])

  const handleDocumentUpload = useCallback(async () => {
    if (!selectedFile || !evidenceForm.title) return

    await onUploadDocument(selectedFile, {
      ...evidenceForm,
      // Parent prop wins; otherwise form value; null → General.
      kpaId: kpaId !== undefined ? kpaId : (evidenceForm.kpaId || undefined),
      documentType: getFileType(selectedFile.name),
      version: 1,
      validationStatus: userRole === 'manager' ? 'validated' : 'pending'
    })
    
    setSelectedFile(null)
    resetForm(true) // Close dialog after direct document upload
    setShowDocumentUpload(false)
  }, [selectedFile, evidenceForm, kpaId, userRole, onUploadDocument])

  const getFileType = (filename: string): EnhancedEvidenceEntry['documentType'] => {
    const ext = filename.split('.').pop()?.toLowerCase()
    switch (ext) {
      case 'pdf': return 'pdf'
      case 'doc':
      case 'docx': return 'word'
      case 'xls':
      case 'xlsx': return 'excel'
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'image'
      default: return 'other'
    }
  }

  const resetForm = (closeDialog = false) => {
    setEvidenceForm(blankEvidenceForm())
    setMeasurementValue('')
    setSelectedFile(null)
    setEditingEvidence(null)
    // Each fresh Add starts with the advanced section collapsed so Simple
    // CREATE opens on the calm 4-field + attachment form (rework task #13).
    setShowAdditionalFields(false)
    // Always reset the add path so a stale 'measurement' choice doesn't
    // default the next general Add to measurement type.
    setAddMode('entry')
    if (closeDialog) {
      setShowEvidenceForm(false)
    }
  }

  const startEdit = (evidence: EnhancedEvidenceEntry) => {
    // Check if evidence is from a locked period BEFORE opening the modal
    if (checkEvidenceEditable) {
      const editCheck = checkEvidenceEditable(evidence)
      if (!editCheck.canEdit) {
        toast.error(editCheck.reason || 'Cannot edit this entry')
        return
      }
    }
    
    setEditingEvidence(evidence)
    setEvidenceForm({
      type: evidence.type,
      classification: evidence.classification,
      source: evidence.source,
      title: evidence.title,
      description: evidence.description,
      impact: evidence.impact,
      evidence: evidence.evidence,
      externalLink: evidence.externalLink,
      kpiId: evidence.kpiId, // Preserve KPI link
      objectiveId: evidence.objectiveId,
      kpaId: evidence.kpaId,
      reviewPeriod: evidence.reviewPeriod || reviewPeriod,
      appliesToBothPeriods: evidence.appliesToBothPeriods,
      // Phase 2 — preserve per-period values so an edit on one period
      // doesn't accidentally null out the other period's frozen value.
      midyearMeasurementValue: evidence.midyearMeasurementValue,
      yearendMeasurementValue: evidence.yearendMeasurementValue,
    })

    // If this is a KPI measurement, populate the measurement input with
    // the value for the period currently being VIEWED (not the legacy
    // `measurement_value`). This is what makes the year-end editor show
    // the year-end value when you're in year-end view, and the mid-year
    // value when you're in mid-year view — even on the same multi-period
    // entry. Falls back to the legacy `measurementValue` for older rows
    // that don't yet have the per-period columns populated.
    if (evidence.type === 'measurement' && evidence.kpiId) {
      const periodValue = reviewPeriod === 'YEAREND'
        ? evidence.yearendMeasurementValue
        : evidence.midyearMeasurementValue
      if (periodValue !== undefined && periodValue !== null) {
        setMeasurementValue(String(periodValue))
      } else if (evidence.measurementValue !== undefined && evidence.measurementValue !== null) {
        setMeasurementValue(String(evidence.measurementValue))
      } else {
        // Legacy fallback: parse a number out of the formatted evidence string
        const numericMatch = evidence.evidence?.match(/\d+\.?\d*/)
        if (numericMatch) {
          setMeasurementValue(numericMatch[0])
        }
      }
    }

    setShowEvidenceForm(true)
  }

  // True when the currently-viewed period's half is captured in the
  // historical record. The user can still edit the OTHER half of a
  // dual-period entry, so this is NOT a blanket lock — it gates only the
  // fields belonging to the locked period (handled in the dialog).
  const lockedForCurrentView = (evidence: EnhancedEvidenceEntry): boolean => {
    if (evidence.lockState === 'fully_locked') return true
    if (reviewPeriod === 'MIDYEAR' && evidence.lockState === 'midyear_locked') return true
    if (reviewPeriod === 'YEAREND' && evidence.lockState === 'yearend_locked') return true
    // Back-compat: if the server didn't send lockState, fall back to the
    // legacy boolean.
    if (!evidence.lockState && evidence.lockedForReview) return true
    return false
  }

  // Feedback-derived entries mirror what stakeholders submitted — they are
  // read-only. The team member can't edit or delete the results; the only
  // lever is release (share for a check-in, or leave private). Enforced
  // server-side too (evidence update/delete return 403).
  const isFeedbackDerived = (evidence: EnhancedEvidenceEntry) =>
    !!(evidence.sourceFeedbackRequestId || evidence.sourceFeedbackResponseId)

  const canEditEvidence = (evidence: EnhancedEvidenceEntry) => {
    if (readOnly) return false
    if (isFeedbackDerived(evidence)) return false
    if (evidence.carriedFromMidYear) return false  // carried-forward context is read-only here
    // Fully-locked: no half is live, nothing to edit. Use carry-forward.
    if (evidence.lockState === 'fully_locked') return false
    if (!evidence.lockState && evidence.lockedForReview) return false
    const effectiveUserId = currentUserId || targetUserId
    if (!effectiveUserId) return false
    // Original creator can always edit
    if (evidence.createdBy === effectiveUserId) return true
    // Employee viewing own profile can edit their own employee-source evidence
    // (tolerates ID format drift between createdBy and currentUserId)
    if (evidence.source === 'employee' && targetUserId && targetUserId === effectiveUserId) return true
    return false
  }

  const canDeleteEvidence = (evidence: EnhancedEvidenceEntry) => {
    if (readOnly) return false
    if (isFeedbackDerived(evidence)) return false
    if (evidence.carriedFromMidYear) return false
    // ANY lock state blocks delete — even the dual-period half-locked
    // states. The locked side's historical record needs the row to stay.
    if (evidence.lockState && evidence.lockState !== 'unlocked') return false
    if (!evidence.lockState && evidence.lockedForReview) return false
    const effectiveUserId = currentUserId || targetUserId
    if (!effectiveUserId) return false
    if (evidence.createdBy === effectiveUserId) return true
    if (evidence.source === 'employee' && targetUserId && targetUserId === effectiveUserId) return true
    return false
  }

  const canClassifyEvidence = (evidence: EnhancedEvidenceEntry) => {
    if (!onClassifyEvidence) return false
    // Managers can classify any evidence, employees can only classify their own
    if (userRole === 'manager') return true
    const effectiveUserId = currentUserId || targetUserId
    return evidence.createdBy === effectiveUserId
  }

  // Add-Entry permission rules:
  //   * Impact Portfolio mode (default) — only the portfolio owner can add
  //     general (non-measurement) entries. People Leaders viewing a team
  //     member's portfolio see no general Add button; they add
  //     **Observations** via the separate observations surface (see
  //     `observationsMode` below).
  //   * Observations mode — only the *People Leader* (≠ portfolio owner) can
  //     add. Used by the PL Journey "Leader Observations" tab where the PL
  //     records private notes about the team member.
  // Server-side authorship is derived from the authenticated identity, so
  // there's no client-side trust required; this is purely the UI affordance.
  const canAddEntry = !!currentUserId && !!targetUserId && (
    observationsMode
      ? currentUserId !== targetUserId
      : currentUserId === targetUserId
  )

  // Per-KPI measurement add path. When this surface is bound to a single KPI
  // (`preSelectedKPIId` set — e.g. the per-KPI dialog inside a check-in),
  // adding is inherently a measurement: a KPI number with its backing
  // evidence. Per the agreed access model a measurement is visible to BOTH
  // the team member and the people leader, so EITHER party may record one.
  // A PL-added measurement is source='manager' + KPI + value and renders in
  // the Impact Portfolio (attributed to the PL), not in Observations. The
  // general (non-measurement) Add stays owner-only via `canAddEntry`.
  const canAddMeasurement = !readOnly && !observationsMode &&
    !!currentUserId && !!targetUserId
  // Standalone "Record Measurement" button — only when there's NO pre-locked
  // KPI. When a KPI is pre-locked (`preSelectedKPIId`) the header renders the
  // single "Add Measurement" button (old behaviour); when it isn't, the user
  // gets a "Record Measurement" button that opens the dialog in measurement
  // mode and lets them pick the KPI in-dialog (discoverable measurement entry).
  const canShowRecordMeasurementButton = canAddMeasurement && !preSelectedKPIId

  // One warm description voice, shared across Simple and Detailed so the two
  // densities read as one surface (rework task #10). Observations + manager
  // read-only surfaces keep their own framing.
  //
  // The team-member Impact Portfolio is wrapped by `PortfolioTab`, whose
  // page-header already carries the "Capture your evidence … you decide what to
  // release at your next check-in" description. Rendering it again at this
  // section level produced a duplicate paragraph on that surface, so we let
  // PortfolioTab own the description and emit `null` here. Observations and
  // manager read-only surfaces have no such wrapper, so they keep their own
  // framing line (rendered only when non-null below).
  const surfaceDescription = observationsMode
    ? 'Your private notes about this team member. Only you can see these.'
    : userRole === 'manager'
      ? "Your team member's portfolio"
      : null

  // Shared action buttons — "Upload Document" + "Add Entry" labels in BOTH
  // views (the all-caps "NEW ENTRY" / bare "Upload" variants are dropped).
  // The old "Viewing All Entries" visibility filter pill was removed with
  // the user-facing visibility control — there is no per-entry private/shared
  // flag to toggle any more.
  const headerActions = (
    <>
      {!hideUploadButton && canAddEntry && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowDocumentUpload(true)}
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
      )}
      {/* Measurement add path:
          - KPI pre-locked (preSelectedKPIId): single "Add Measurement" button
            (the surface is bound to one KPI, so adding is inherently a
            measurement). This is the old per-KPI behaviour.
          - No pre-locked KPI but user may record measurements: a discoverable
            "Record Measurement" button that opens the dialog in measurement
            mode and lets them pick the KPI in-dialog. */}
      {!readOnly && canAddMeasurement && preSelectedKPIId && (
        <Button
          size="sm"
          onClick={() => {
            // Bind the form to this surface's KPI every time, so New Value shows.
            if (!editingEvidence) setEvidenceForm(blankEvidenceForm('measurement'))
            setAddMode('measurement')
            setShowEvidenceForm(true)
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Measurement
        </Button>
      )}
      {!readOnly && canShowRecordMeasurementButton && (
        <Button
          size="sm"
          variant={canAddEntry ? 'outline' : 'default'}
          onClick={() => { setAddMode('measurement'); setShowEvidenceForm(true) }}
        >
          <Target className="h-4 w-4 mr-2" />
          Record Measurement
        </Button>
      )}
      {!readOnly && canAddEntry && (
        <Button
          size="sm"
          onClick={() => { setAddMode('entry'); setShowEvidenceForm(true) }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Entry
        </Button>
      )}
    </>
  )

  // Simple-mode CREATE collapses the Add Entry dialog to the essentials
  // (rework task #13): Title, Description, Link to KPA, Link to KPI,
  // Attachment. Entry Type / Visibility / Entry Source / Additional Details
  // hide behind an "Add more detail" disclosure. Editing always shows the
  // full form so existing classifications/links stay editable, and Detailed
  // mode keeps the full form too.
  const simpleCreate = viewMode === 'simple' && !editingEvidence
  // In simple-create, the advanced block is gated behind "Add more detail";
  // in detailed / edit it's always shown. We reuse `showAdditionalFields` as
  // the single disclosure toggle for the whole advanced section.
  const showAdvanced = !simpleCreate || showAdditionalFields

  return (
    <div className={viewMode === 'simple' ? 'py-2 space-y-8' : 'p-6 space-y-6'}>
      {/* Header — one surface at two densities. Simple shows a quiet
          editorial section label (the big "my Impact Portfolio" headline is
          owned by the surrounding PortfolioTab). Detailed shows the same
          quiet label INSTEAD of a duplicate "Impact Portfolio" H2 (rework
          task #12 — the H1 already lives in PortfolioTab). Both share the
          warm description copy, the green filter pill, and the
          "Add Entry" / "Upload Document" actions. */}
      {viewMode === 'simple' ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase">
              {observationsMode ? 'Leader Observations' : 'Entries'}
              {userRole === 'manager' && targetUserId && !observationsMode && <span className="ml-2">· Team member</span>}
              {observationsMode && <span className="ml-2">· Private to you</span>}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {headerActions}
            </div>
          </div>
          {surfaceDescription && (
            <p className="font-editorial italic text-quarterly-ink-muted text-[15px] leading-[1.55] max-w-[600px]">
              {surfaceDescription}
            </p>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase">
              {observationsMode ? 'Leader Observations' : 'Entries'}
              {userRole === 'manager' && targetUserId && !observationsMode && <span className="ml-2">· Team member</span>}
              {observationsMode && <span className="ml-2">· Private to you</span>}
            </p>
            {surfaceDescription && (
              <p className="text-gray-600 mt-1 max-w-xl">
                {surfaceDescription}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            {headerActions}
          </div>
        </div>
      )}

      {/* Manager-cannot-add notice — only shown when explicitly requested, and
          never in observations mode (where the PL IS the one allowed to add). */}
      {showManagerCannotAddNotice && !observationsMode && !canAddEntry && !readOnly && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          {canAddMeasurement ? (
            <>
              <span className="font-medium">You can record KPI measurements here.</span>{' '}
              Only {ownerDisplayName ? ownerDisplayName : 'the team member'} can add narrative impact entries — you'll discuss those together at the next check-in.
            </>
          ) : (
            <>
              <span className="font-medium">Read-only.</span>{' '}
              Only {ownerDisplayName ? ownerDisplayName : 'the team member'} can add entries to their portfolio. You'll discuss them together at the next check-in.
            </>
          )}
        </div>
      )}

      {/* Classification legend deliberately removed — the portfolio is a
          judgement-free space; we don't display the 4-tier rating UI in
          either Simple or Detailed view. */}

      {/* Filter bar — only on the top-level mixed view (kpaId undefined),
          shared by the team member's "my Impact Portfolio" and the People
          Leader's portfolio tab. Filter by area (KPA / General), entry type,
          and free-text search. Per-KPA panels don't show this. */}
      {kpaId === undefined && evidence.length > 0 && (() => {
        const typeLabels: Record<string, string> = {
          achievement: 'Achievement',
          document: 'Document',
          link: 'Link',
          stakeholder_feedback: 'Stakeholder Feedback',
          project_outcome: 'Project Outcome',
          measurement: 'Measurement',
        }
        const presentTypes = Array.from(new Set(evidence.map(e => e.type).filter(Boolean)))
        const hasActiveFilter = kpaFilter !== 'all' || typeFilter !== 'all' || !!portfolioSearch
        return (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Select value={kpaFilter} onValueChange={setKpaFilter}>
              <SelectTrigger className="h-9 w-auto min-w-[140px] text-sm">
                <SelectValue placeholder="All areas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All areas</SelectItem>
                {(kpaOptions || []).map(o => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
            {presentTypes.length > 1 && (
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-9 w-auto min-w-[140px] text-sm">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {presentTypes.map(t => (
                    <SelectItem key={t} value={t}>{typeLabels[t] || t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Input
              value={portfolioSearch}
              onChange={(e) => setPortfolioSearch(e.target.value)}
              placeholder="Search entries…"
              className="h-9 w-[180px] text-sm"
            />
            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setKpaFilter('all'); setTypeFilter('all'); setPortfolioSearch('') }}
                className="h-9 text-sm text-gray-500"
              >
                Clear
              </Button>
            )}
          </div>
        )
      })()}

      {/* Evidence List */}
      <div className="space-y-4">
        {(() => {
          // Per-entry visibility is decided server-side from source +
          // measurement status + active releases (the agreed access
          // model). The client no longer gates access on a `visibility`
          // flag — it only partitions the already-authorised list into the
          // two surfaces below.
          let filteredEvidence = evidence

          // Surface partitioning between the two surfaces this component
          // serves — MEASUREMENT-AWARE per the agreed access model. The
          // parent passes a single combined list (the server returns
          // team-member entries, the PL's own observations, AND every
          // measurement when a PL queries a team member's evidence). We
          // split client-side so each surface shows only its own slice:
          //   - Impact Portfolio  = source!='manager' OR isMeasurement
          //     (TM entries AND every measurement, including PL-recorded
          //     ones — a measurement is the backing evidence for a KPI
          //     number, so it belongs in the portfolio attributed to its
          //     author, never hidden away in Observations).
          //   - Leader Observations = source=='manager' AND !isMeasurement
          //     (PL qualitative notes only).
          // A measurement is any KPI-linked entry that carries a value in
          // the legacy `measurementValue` OR either per-period column.
          const isMeasurement = (e: EnhancedEvidenceEntry): boolean =>
            !!e.kpiId && (
              e.measurementValue != null ||
              e.midyearMeasurementValue != null ||
              e.yearendMeasurementValue != null
            )
          if (observationsMode) {
            filteredEvidence = filteredEvidence.filter(e => e.source === 'manager' && !isMeasurement(e))
          } else {
            filteredEvidence = filteredEvidence.filter(e => e.source !== 'manager' || isMeasurement(e))
          }

          // Filter by review period if specified. Three buckets surface in
          // the current period view:
          //   1. Legacy entries with no `reviewPeriod` set
          //   2. Entries whose originating period matches the current view
          //   3. Entries opted into both periods via `appliesToBothPeriods`
          // Skipped on release-gated surfaces (the PL's top-level portfolio),
          // which already filtered by RELEASE — capture period is irrelevant
          // there and would wrongly hide cross-period released entries.
          if (reviewPeriod && !skipPeriodDisplayFilter) {
            filteredEvidence = filteredEvidence.filter(e =>
              !e.reviewPeriod ||
              e.reviewPeriod === reviewPeriod ||
              e.appliesToBothPeriods === true ||
              e.carriedFromMidYear === true  // Mid-Year measurement shown in YE as carried context
            )
          }

          // Top-level portfolio filters (mixed view only). The KPA / type /
          // search controls live in the filter bar above and apply to both the
          // team member's and the People Leader's portfolio tab.
          if (kpaId === undefined) {
            if (kpaFilter === 'general') {
              filteredEvidence = filteredEvidence.filter(e => !e.kpaId)
            } else if (kpaFilter !== 'all') {
              filteredEvidence = filteredEvidence.filter(e => kpaOptionFor(e.kpaId)?.id === kpaFilter)
            }
            if (typeFilter !== 'all') {
              filteredEvidence = filteredEvidence.filter(e => e.type === typeFilter)
            }
            const q = portfolioSearch.trim().toLowerCase()
            if (q) {
              filteredEvidence = filteredEvidence.filter(e =>
                (e.title || '').toLowerCase().includes(q) ||
                (e.description || '').toLowerCase().includes(q)
              )
            }
          }

          return filteredEvidence.length === 0 ? (
          viewMode === 'simple' ? (
            <div className="py-12 text-center">
              <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase mb-3">
                Nothing yet
              </p>
              <p className="font-editorial italic text-quarterly-ink-muted text-[18px] leading-[1.55] max-w-[480px] mx-auto mb-6">
                {observationsMode
                  ? "You haven't added any observations yet."
                  : userRole === 'manager'
                  ? "Your team member hasn't shared any entries here yet."
                  : 'Start with one moment from this week — a win, a lesson, a conversation that mattered.'}
              </p>
              {!readOnly && (canAddEntry || canAddMeasurement) && (
                <button
                  onClick={() => {
                    // Empty-state CTA mirrors the header's primary path: if the
                    // only thing this user can do is record measurements (no
                    // general Add), open in measurement mode; otherwise open the
                    // general Add path.
                    setAddMode(canAddEntry ? 'entry' : 'measurement')
                    setShowEvidenceForm(true)
                  }}
                  className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.22em] uppercase text-quarterly-tobacco hover:underline"
                >
                  <Plus className="h-3 w-3" />
                  {canAddMeasurement && !canAddEntry ? 'Add Measurement' : 'Add Entry'}
                </button>
              )}
            </div>
          ) : (
            <Card className="border-2 border-dashed border-gray-300">
              <CardContent className="p-8 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {observationsMode ? 'No Observations Yet' : 'No Entries Yet'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {observationsMode
                    ? 'Private notes you capture here stay with you.'
                    : userRole === 'manager'
                    ? 'Help your team member build their impact portfolio'
                    : 'Start building your impact portfolio to track progress'
                  }
                </p>
                {!readOnly && (
                  <Button onClick={() => setShowEvidenceForm(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Entry
                  </Button>
                )}
              </CardContent>
            </Card>
          )
        ) : (
          filteredEvidence.map((evidenceItem) => (
            <Card
              key={evidenceItem.id}
              id={`evidence-${evidenceItem.id}`}
              className={viewMode === 'simple'
                ? 'border-0 border-t border-stone-200 first:border-t-0 rounded-none shadow-none bg-transparent transition-colors'
                : 'border-l-4 border-l-heritage-blue transition-all'}
            >
              <CardContent className={viewMode === 'simple' ? 'px-0 py-7' : 'p-6'}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className={viewMode === 'simple'
                        ? 'font-serif text-[22px] md:text-[24px] leading-[1.2] tracking-tight text-quarterly-ink'
                        : 'text-lg font-semibold text-gray-900'}>
                        {evidenceItem.title}
                      </h3>
                      
                      {/* KPA / General chip — shown on the top-level
                          Portfolio surface where entries from different KPAs
                          (and General) are mixed in one list. Per-KPA
                          surfaces already group by KPA so the chip would be
                          redundant; we suppress it there by gating on
                          `kpaId === undefined` (parent isn't bound to a KPA).
                          A Leader Observation shows it everywhere, so it
                          always says which KPA it is about (HR UAT list #2).
                          The "General category" chip directly addresses HR
                          feedback to surface non-KPA impact items. */}
                      {(kpaId === undefined || observationsMode) && (() => {
                        if (!evidenceItem.kpaId) {
                          return (
                            <Badge
                              variant="outline"
                              className={viewMode === 'simple'
                                ? 'bg-transparent text-quarterly-ink-muted border-quarterly-rule rounded-none text-[10px] tracking-[0.18em] uppercase font-medium'
                                : 'bg-stone-50 text-stone-700 border-stone-300'}
                              title="General entry — not tied to a specific KPA"
                            >
                              General
                            </Badge>
                          )
                        }
                        const opt = kpaOptionFor(evidenceItem.kpaId)
                        if (!opt) return null
                        return (
                          <Badge
                            variant="outline"
                            className={viewMode === 'simple'
                              ? 'bg-transparent text-quarterly-ink-muted border-quarterly-rule rounded-none text-[10px] tracking-[0.18em] uppercase font-medium'
                              : 'bg-blue-50 text-blue-800 border-blue-200'}
                            title={`Linked to KPA: ${opt.name}`}
                          >
                            {opt.name}
                          </Badge>
                        )
                      })()}

                      {/* KPI chip — A4 (May 2026 rework). Surfaces the KPI
                          link when present, so users skimming the entry list
                          can see at a glance which measure the entry feeds
                          into. Shown alongside (and visually subordinate to)
                          the KPA chip — KPA is the category, KPI is the
                          specific measure within it. */}
                      {evidenceItem.kpiId && (() => {
                        const kpi = availableKPIs.find((k) => k.id === evidenceItem.kpiId)
                        if (!kpi) return null
                        return (
                          <Badge
                            variant="outline"
                            className={viewMode === 'simple'
                              ? 'bg-transparent text-emerald-700 border-emerald-300 rounded-none text-[10px] tracking-[0.18em] uppercase font-medium inline-flex items-center gap-1'
                              : 'bg-emerald-50 text-emerald-800 border-emerald-200 inline-flex items-center gap-1'}
                            title={`Linked to KPI: ${kpi.name}`}
                          >
                            <Target className="h-3 w-3" />
                            {kpi.name}
                          </Badge>
                        )
                      })()}

                      {/* Classification badge intentionally removed — the
                          portfolio is a judgement-free space. Source/validation
                          chips still surface in Detailed view for context. */}
                      {viewMode === 'detailed' && (
                        <>
                          <Badge variant={evidenceItem.source === 'employee' ? 'default' : 'secondary'}>
                            {evidenceItem.source === 'employee' ? 'Team Member Entry' : 'People Leader Entry'}
                          </Badge>

                          {evidenceItem.validationStatus === 'validated' && (
                            <Badge className="bg-blue-100 text-blue-800">
                              <CheckSquare className="h-3 w-3 mr-1" />
                              Validated
                            </Badge>
                          )}
                        </>
                      )}

                      {/* The "Private to you" / "Shared" (Confidential /
                          Visible) chip was removed with the user-facing
                          visibility control. Privacy is no longer a
                          per-entry user choice: a narrative is private to the
                          team member until released, a measurement is shared
                          with the people leader as KPI backing evidence, and
                          a PL observation is private to the PL — all decided
                          by source + measurement + release state, surfaced by
                          the release control rather than a static chip. */}

                      {/* (Removed the duplicate purple KPI badge — the green
                          KPI chip above already surfaces the linked KPI.) */}

                      {/* Phase 1: dual-period chip. Makes it visible at a glance
                          that this entry will surface in both check-in views. */}
                      {evidenceItem.appliesToBothPeriods && (
                        <Badge variant="outline" className={viewMode === 'simple'
                          ? 'bg-transparent text-quarterly-tobacco border-quarterly-tobacco/40 rounded-none text-[10px] tracking-[0.18em] uppercase font-medium'
                          : 'bg-blue-50 text-blue-700 border-blue-300'}>
                          Mid-Year & Year-End
                        </Badge>
                      )}

                      {/* Carried-forward marker — a Mid-Year measurement shown in
                          the Year-End view for a continuing objective. Read-only. */}
                      {evidenceItem.carriedFromMidYear && (
                        <Badge variant="outline" className={viewMode === 'simple'
                          ? 'bg-transparent text-amber-700 border-amber-300 rounded-none text-[10px] tracking-[0.18em] uppercase font-medium inline-flex items-center gap-1'
                          : 'bg-amber-50 text-amber-700 border-amber-300 inline-flex items-center gap-1'}
                          title="This Mid-Year measurement carries forward because the objective runs past June. Record a Year-End measurement to update it.">
                          <ArrowRight className="h-3 w-3" />
                          Carried forward from Mid-Year
                        </Badge>
                      )}

                      {/* Release state lives in one place: the binary
                          Shared / Private toggle in the actions column. */}
                    </div>

                    {evidenceItem.type === 'stakeholder_feedback' ? (
                      <StakeholderFeedbackBody
                        description={evidenceItem.description || ''}
                        viewMode={viewMode === 'simple' ? 'simple' : 'detailed'}
                      />
                    ) : (
                      <p className={viewMode === 'simple'
                        ? 'font-editorial italic text-quarterly-ink-muted text-[17px] leading-[1.55] mb-4 max-w-[600px]'
                        : 'text-gray-600 mb-4'}>
                        {evidenceItem.description}
                      </p>
                    )}
                    
                    {/* Show KPI Measurement Progression when evidence is linked to KPI */}
                    {evidenceItem.kpiId && (() => {
                      const kpi = availableKPIs.find(k => k.id === evidenceItem.kpiId)
                      const evidenceData = evidenceItem as any

                      // Phase 2: prefer the period-specific column for the
                      // currently-viewed period. Falls back to the legacy
                      // `measurementValue` so older rows that haven't been
                      // edited under the new model still render.
                      const periodSpecificValue = reviewPeriod === 'YEAREND'
                        ? evidenceData.yearendMeasurementValue
                        : evidenceData.midyearMeasurementValue
                      let newValue = periodSpecificValue ?? evidenceData.measurementValue
                      let previousValue = evidenceData.previousValue
                      // Try to find the corresponding measurement in the KPI's measurement history
                      let historyEntry = null
                      
                      if (kpi?.measurement_history) {
                        try {
                          let historyData = typeof kpi.measurement_history === 'string' 
                            ? JSON.parse(kpi.measurement_history) 
                            : kpi.measurement_history
                          
                          if (Array.isArray(historyData)) {
                            // Filter by review period if specified
                            if (reviewPeriod) {
                              historyData = historyData.filter((entry: any) => 
                                !entry.review_period || entry.review_period === reviewPeriod
                              )
                            }
                            
                            // First try: Find the MOST RECENT measurement entry that matches this evidence ID
                            // (Evidence can be updated multiple times, creating multiple history entries)
                            const matchingEntries = historyData.filter((entry: any) => entry.evidence_id === evidenceItem.id)
                            if (matchingEntries.length > 0) {
                              // Sort by timestamp descending and get the most recent
                              matchingEntries.sort((a: any, b: any) => 
                                new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                              )
                              historyEntry = matchingEntries[0]
                            }
                            
                            // Second try: If no exact match, find the most recent measurement before or at this evidence timestamp
                            if (!historyEntry && historyData.length > 0) {
                              const evidenceTimestamp = new Date(evidenceItem.date || evidenceItem.updatedAt).getTime()
                              
                              // Sort by timestamp descending and find the first one <= evidence timestamp
                              const sortedHistory = [...historyData].sort((a: any, b: any) => 
                                new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                              )
                              
                              historyEntry = sortedHistory.find((entry: any) => 
                                new Date(entry.timestamp).getTime() <= evidenceTimestamp
                              ) || sortedHistory[sortedHistory.length - 1] // Fallback to earliest if none found
                            }
                            
                            if (historyEntry) {
                              // Use values from measurement history for accuracy (only if they exist)
                              if (historyEntry.value != null) {
                                newValue = historyEntry.value
                              }
                              if (historyEntry.previous != null) {
                                previousValue = historyEntry.previous
                              }
                            }
                          }
                        } catch (e) {
                          console.warn('Failed to parse measurement history:', e)
                        }
                      }
                      
                      // Fallback: Get new value from measurementValue field or extract from evidence text
                      if (newValue == null) {
                        const match = evidenceItem.evidence?.match(/\d+\.?\d*/)
                        newValue = match ? parseFloat(match[0]) : null
                      }
                      
                      // If this is measurement type but no value, show prompt.
                      // Phase 2: when the entry IS multi-period and the OTHER
                      // period has a value, surface that fact and prompt the
                      // user to add a value for the period currently in view —
                      // this is the "Add year-end value" affordance for an
                      // entry whose only measurement so far was at mid-year.
                      if (kpi && evidenceData.type === 'measurement' && (newValue === null || newValue === undefined)) {
                        const otherPeriodLabel = reviewPeriod === 'YEAREND' ? 'Year-End' : 'Mid-Year'
                        const otherPeriodValue = reviewPeriod === 'YEAREND'
                          ? evidenceData.midyearMeasurementValue
                          : evidenceData.yearendMeasurementValue
                        const hasOtherPeriodValue = otherPeriodValue !== null && otherPeriodValue !== undefined
                        const isDualPeriod = evidenceData.appliesToBothPeriods

                        if (isDualPeriod && hasOtherPeriodValue) {
                          const otherLabel = reviewPeriod === 'YEAREND' ? 'Mid-Year' : 'Year-End'
                          return (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center gap-2 text-blue-900">
                                <AlertCircle className="h-4 w-4" />
                                <span className="text-sm font-medium">No {otherPeriodLabel} value yet</span>
                              </div>
                              <p className="text-xs text-blue-800 mt-1">
                                {otherLabel} value: <span className="font-semibold">{formatMeasurementValue(otherPeriodValue, inferMeasurementMode(kpi), kpi.unit)}</span>.
                                Edit this entry while in {otherPeriodLabel} view to add a fresh measurement —
                                the {otherLabel} value will be preserved.
                              </p>
                            </div>
                          )
                        }

                        return (
                          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-2 text-amber-800">
                              <AlertCircle className="h-4 w-4" />
                              <span className="text-sm font-medium">No measurement value recorded</span>
                            </div>
                            <p className="text-xs text-amber-700 mt-1">Edit this entry to add a KPI measurement value.</p>
                          </div>
                        );
                      }
                      
                      // Fallback for previous value if not found in history or evidence
                      // Note: For binary KPIs, 0 means "Not Achieved" and 100 means "Achieved"
                      // Only use kpi?.current_value as fallback if it's actually set
                      if (previousValue == null && kpi?.current_value != null) {
                        previousValue = kpi.current_value
                      }
                      // If still null, default to 0 (first measurement scenario)
                      if (previousValue == null) {
                        previousValue = 0
                      }
                      
                      // Show if we have KPI and new value (previousValue can be 0 for first measurement)
                      // Infer measurement mode for display formatting
                      const kpiMode = inferModeFromUnit(kpi?.unit);
                      const isRatingOrBinaryKPI = kpiMode === 'rating' || kpiMode === 'binary';
                      
                      // Use period-specific KPI value for "Current KPI Value" display
                      // This ensures mid-year view shows mid-year value, not the overall current value
                      const periodCurrentValue = reviewPeriod === 'MIDYEAR' 
                        ? ((kpi as any)?.midyear_value ?? kpi?.current_value)
                        : ((kpi as any)?.yearend_value ?? kpi?.current_value);
                      
                      return kpi && newValue !== null ? (
                        <div className="mb-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg shadow-sm overflow-hidden">
                          {/* Compact Header with Icon */}
                          <div className="px-3 py-2 bg-white/60 backdrop-blur-sm border-b border-emerald-200/50 flex items-center gap-2">
                            <div className="p-1 bg-emerald-100 rounded">
                              <Target className="h-3.5 w-3.5 text-emerald-700" />
                            </div>
                            <h4 className="text-xs font-semibold text-emerald-900">KPI Measurement</h4>
                          </div>
                          
                          {/* Compact Measurement Display */}
                          <div className="px-3 py-3">
                            {/* Timestamp */}
                            <div className="flex items-center gap-1.5 mb-2 text-[10px] text-gray-500">
                              <Clock className="h-3 w-3" />
                              <span>Submitted: {(() => {
                                const dateStr = evidenceItem.date || evidenceItem.updatedAt;
                                // Add 'Z' if not present to indicate UTC
                                const utcDate = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
                                return utcDate.toLocaleString('en-ZA', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                });
                              })()}</span>
                            </div>
                            
                            <div className="flex items-center gap-3 mb-2">
                              {/* Previous → Submitted */}
                              <div className="flex-1 flex items-center gap-2">
                                <div>
                                  <div className="text-[10px] font-medium text-gray-500 uppercase">Previous</div>
                                  <div className={`text-lg font-bold ${isRatingOrBinaryKPI ? measurementColor(previousValue, kpiMode, 'text') : 'text-gray-700'}`}>
                                    {formatMeasurementValue(previousValue, kpiMode, kpi.unit)}
                                  </div>
                                </div>
                                <div className={`text-xl font-bold ${newValue > previousValue ? 'text-emerald-600' : newValue < previousValue ? 'text-amber-600' : 'text-gray-400'}`}>
                                  →
                                </div>
                                <div>
                                  <div className="text-[10px] font-medium text-blue-700 uppercase flex items-center gap-1">
                                    Submitted
                                    {/* Phase 2: when the entry is dual-period, label the value
                                        with which period it belongs to so users see at a glance
                                        whether they're looking at the mid-year or year-end value. */}
                                    {(evidenceItem as any).appliesToBothPeriods && (
                                      <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-blue-100 text-blue-800">
                                        {reviewPeriod === 'YEAREND' ? 'YE' : 'MY'}
                                      </span>
                                    )}
                                  </div>
                                  <div className={`text-lg font-bold ${isRatingOrBinaryKPI ? measurementColor(newValue, kpiMode, 'text') : 'text-blue-700'}`}>
                                    {formatMeasurementValue(newValue, kpiMode, kpi.unit)}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Change Badge - hide for rating/binary, show status instead */}
                              {isRatingOrBinaryKPI ? (
                                <div className={`px-2 py-1 rounded text-xs font-bold ${measurementColor(newValue, kpiMode, 'bg')} ${measurementColor(newValue, kpiMode, 'text')}`}>
                                  {formatMeasurementValue(newValue, kpiMode)}
                                </div>
                              ) : newValue !== previousValue && (
                                <div className={`px-2 py-1 rounded text-xs font-bold ${newValue > previousValue ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {newValue > previousValue ? '+' : ''}{(newValue - previousValue).toFixed(1)}{kpi.unit ? ` ${kpi.unit}` : ''}
                                </div>
                              )}
                            </div>
                            
                            {/* Current KPI Value Badge - Always show for clarity */}
                            {/* Uses period-specific value (midyear_value or yearend_value) based on reviewPeriod */}
                            {periodCurrentValue != null && (
                              <div className={`mb-3 p-2 rounded-lg flex items-center justify-between ${
                                periodCurrentValue === newValue 
                                  ? 'bg-blue-50 border border-blue-200' 
                                  : 'bg-emerald-50 border border-emerald-200'
                              }`}>
                                <div className="flex items-center gap-2">
                                  <div className={`p-1 rounded ${periodCurrentValue === newValue ? 'bg-blue-100' : 'bg-emerald-100'}`}>
                                    <Target className={`h-3 w-3 ${periodCurrentValue === newValue ? 'text-blue-700' : 'text-emerald-700'}`} />
                                  </div>
                                  <span className={`text-xs font-medium ${periodCurrentValue === newValue ? 'text-blue-900' : 'text-emerald-900'}`}>
                                    Current KPI Value:
                                  </span>
                                </div>
                                <div className={`text-sm font-bold ${isRatingOrBinaryKPI ? measurementColor(periodCurrentValue, kpiMode, 'text') : (periodCurrentValue === newValue ? 'text-blue-700' : 'text-emerald-700')}`}>
                                  {formatMeasurementValue(periodCurrentValue, kpiMode, kpi.unit)}
                                  {!isRatingOrBinaryKPI && periodCurrentValue !== newValue && (
                                    <span className="ml-2 text-[10px] font-normal text-emerald-600">
                                      ({periodCurrentValue > newValue ? '+' : ''}{(periodCurrentValue - newValue).toFixed(1)}{kpi.unit ? ` ${kpi.unit}` : ''} since)
                                    </span>
                                  )}
                                  {periodCurrentValue === newValue && (
                                    <span className="ml-2 text-[10px] font-normal text-blue-600">
                                      (this submission)
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Compact Progress Bar */}
                            {(() => {
                              // Get measurement direction for proper progress calculation
                              const kpiDirection = inferMeasurementDirection(kpi);
                              const isLowerBetter = kpiDirection === 'lower_is_better';
                              
                              // Calculate progress based on direction
                              let progressPct = 0;
                              let isAchieved = false;
                              
                              if (isRatingOrBinaryKPI) {
                                progressPct = newValue;
                                isAchieved = newValue >= 100;
                              } else if (kpi.target_value) {
                                if (isLowerBetter) {
                                  // Lower is better: 5 current / 3 target = over target (bad)
                                  // Progress = target / current * 100 (e.g., 3/5 = 60%)
                                  isAchieved = newValue <= kpi.target_value;
                                  progressPct = newValue > 0 ? Math.min(100, (kpi.target_value / newValue) * 100) : 0;
                                } else {
                                  // Higher is better: normal calculation
                                  isAchieved = newValue >= kpi.target_value;
                                  progressPct = Math.min(100, (newValue / kpi.target_value) * 100);
                                }
                              }
                              
                              return (
                                <div className="space-y-1">
                                  <div className="flex items-center justify-between text-[10px]">
                                    <span className="text-gray-600">
                                      Target: {isRatingOrBinaryKPI 
                                        ? (kpiMode === 'rating' ? 'On Track' : 'Achieved')
                                        : `${kpi.target_value}${kpi.unit ? ` ${kpi.unit}` : ''}${isLowerBetter ? ' ↓' : ''}`
                                      }
                                    </span>
                                    <span className={`font-medium ${
                                      viewMode === 'detailed'
                                        ? (isRatingOrBinaryKPI ? measurementColor(newValue, kpiMode, 'text') : (isAchieved ? 'text-emerald-700' : 'text-amber-700'))
                                        // Simple view: emerald ONLY for genuine achievement; otherwise calm stone.
                                        : (isAchieved ? 'text-emerald-700' : 'text-stone-600')
                                    }`}>
                                      {isRatingOrBinaryKPI
                                        ? (newValue >= 100 ? '✓ Achieved' : formatMeasurementValue(newValue, kpiMode))
                                        : (isAchieved ? '✓ Achieved' : kpi.target_value ? `${progressPct.toFixed(0)}%` : 'No target')
                                      }
                                    </span>
                                  </div>
                                  <div className="w-full bg-stone-200 rounded-full h-1.5 overflow-hidden">
                                    <div
                                      className={`h-full transition-all duration-500 ${
                                        viewMode === 'detailed'
                                          ? (isRatingOrBinaryKPI
                                              ? (newValue >= 100 ? 'bg-emerald-500' : newValue >= 50 ? 'bg-amber-500' : 'bg-red-400')
                                              : (isAchieved ? 'bg-emerald-500' : 'bg-amber-500'))
                                          // Simple view: emerald fill only when genuinely achieved; otherwise heritage-blue accent.
                                          : (isAchieved ? 'bg-emerald-500' : 'bg-heritage-blue')
                                      }`}
                                      style={{ width: `${progressPct}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })()}
                            
                            {/* Measurement History - now centralized at KPI level */}
                            {kpi.measurement_history && (
                              <MeasurementHistory history={kpi.measurement_history} unit={kpi.unit} reviewPeriod={reviewPeriod} />
                            )}
                            {reviewPeriod && (
                              <div className="mt-2 text-[10px] text-gray-500 italic">
                                Showing {reviewPeriod === 'MIDYEAR' ? 'Mid-Year' : 'Year-End'} measurements only
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null
                    })()}
                    
                    {evidenceItem.impact && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Impact</h4>
                        <p className="text-sm text-gray-600">{evidenceItem.impact}</p>
                      </div>
                    )}

                    {evidenceItem.externalLink && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-1">External Reference</h4>
                        <a 
                          href={evidenceItem.externalLink.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {evidenceItem.externalLink.title}
                        </a>
                      </div>
                    )}

                    {viewMode === 'simple' ? (
                      // Mono small-caps single-line meta — date, author,
                      // optional editor. Version is omitted in Simple
                      // (versioning is for the audit trail, not the
                      // conversation).
                      <p className="text-[11px] font-medium tracking-[0.22em] text-quarterly-ink-muted uppercase">
                        {formatEntryDate(evidenceItem.date)}
                        <span className="mx-2">·</span>
                        {evidenceItem.createdByName || formatUserName(evidenceItem.createdBy)}
                        {evidenceItem.updatedBy && evidenceItem.updatedBy !== evidenceItem.createdBy && (
                          <>
                            <span className="mx-2">·</span>
                            Edited by {evidenceItem.updatedByName || evidenceItem.updatedBy}
                          </>
                        )}
                      </p>
                    ) : (
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {formatEntryDate(evidenceItem.date)}
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          {evidenceItem.createdByName || formatUserName(evidenceItem.createdBy)}
                          {evidenceItem.updatedBy && evidenceItem.updatedBy !== evidenceItem.createdBy && (
                            <span className="text-xs text-gray-500">
                              (edited by {evidenceItem.updatedByName || evidenceItem.updatedBy})
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Version {evidenceItem.version || 1}
                        </div>
                      </div>
                    )}

                    {/* Lineage chips — explicit thread between a locked
                        Mid-Year source and its editable Year-End follow-up. */}
                    {(evidenceItem.derivedFromEvidenceId || evidenceItem.derivedToEvidenceId) && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {evidenceItem.derivedFromEvidenceId && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800"
                            title="This Year-End entry was carried forward from a Mid-Year entry. The Mid-Year entry remains as the historical record of that conversation."
                          >
                            ← Continued from Mid-Year
                          </span>
                        )}
                        {evidenceItem.derivedToEvidenceId && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800"
                            title="This Mid-Year entry has a Year-End follow-up. It's editable and continues the conversation."
                          >
                            → Continued at Year-End
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions column — capped so the entry body (incl. the KPI
                      Measurement card) gets the lion's share of the width.
                      Pills wrap within the cap; on mobile the whole column
                      drops below the body (the row stacks). */}
                  <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0 sm:justify-end sm:max-w-[200px]">
                    {(() => {
                      const ls = evidenceItem.lockState
                      if (ls === 'midyear_locked') {
                        return (
                          <span
                            className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] text-blue-800"
                            title="The Mid-Year half of this entry is captured in the historical record. The Year-End half is still editable; only the Mid-Year measurement is frozen."
                          >
                            <Lock className="h-3 w-3" />
                            Mid-Year captured
                          </span>
                        )
                      }
                      if (ls === 'yearend_locked') {
                        return (
                          <span
                            className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] text-blue-800"
                            title="The Year-End half of this entry is captured in the historical record. The Mid-Year half is still editable; only the Year-End measurement is frozen."
                          >
                            <Lock className="h-3 w-3" />
                            Year-End captured
                          </span>
                        )
                      }
                      if (ls === 'fully_locked' || (!ls && evidenceItem.lockedForReview)) {
                        return (
                          <span
                            className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-stone-100 px-2 py-0.5 text-[11px] text-stone-700"
                            title="This entry is part of a finalised check-in's historical record. Use 'Continue at Year-End' on a Mid-Year entry to start an editable follow-up."
                          >
                            <Lock className="h-3 w-3" />
                            In a past check-in
                          </span>
                        )
                      }
                      return null
                    })()}
                    {/* Shared / Private — binary release toggle. Owner-only,
                        team-member-source, not observations, not measurements.

                          • Private (outline) → click to share with the People Leader
                          • Shared (emerald, ✓) → click to make private again
                          • locked (stone, lock icon) → the entry's period review
                            has finalised; read-only, no change.
                        Shared = visible to the People Leader in EVERY check-in for
                        the entry's period (capture period + carry-forward decide
                        which period, NOT the session). */}
                    {(() => {
                      if (observationsMode) return null
                      if (evidenceItem.source === 'manager') return null
                      // Measurements are KPI backing data shared automatically —
                      // they're never "released", so no release control on them.
                      if (evidenceItem.kpiId && (
                        evidenceItem.measurementValue != null ||
                        evidenceItem.midyearMeasurementValue != null ||
                        evidenceItem.yearendMeasurementValue != null
                      )) return null
                      const effectiveUserId = currentUserId || targetUserId
                      const isOwner = effectiveUserId && targetUserId && targetUserId === effectiveUserId
                      if (!isOwner) return null
                      if (!onReleaseEvidence && !onWithdrawEvidence) return null

                      // Binary release: a single Shared / Private toggle. Shared =
                      // visible to the People Leader (in every check-in for the
                      // entry's period). Locked once the period's review finalises.
                      const shared = evidenceItem.isReleased === true
                      const locked = !!evidenceItem.lockState && evidenceItem.lockState !== 'unlocked'

                      if (locked) {
                        return (
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-stone-100 px-3 py-1 text-[11px] font-medium text-stone-600"
                            title="This entry is part of a finalised check-in and can no longer be changed."
                          >
                            <Lock className="h-3 w-3" />
                            {shared ? 'Shared' : 'Private'}
                          </span>
                        )
                      }

                      const toggleShare = async () => {
                        try {
                          if (shared) {
                            await onWithdrawEvidence?.(evidenceItem.id)
                            toast.success('Set to private — your people leader can no longer see this.')
                          } else {
                            await onReleaseEvidence?.(evidenceItem.id)
                            toast.success('Shared with your people leader.')
                          }
                        } catch (err: any) {
                          toast.error(err?.response?.data?.detail || err?.message || 'Could not update sharing')
                        }
                      }

                      return (
                        <button
                          type="button"
                          onClick={toggleShare}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium transition-colors',
                            shared
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                              : 'border-stone-300 bg-white text-stone-600 hover:border-emerald-400 hover:text-emerald-800',
                          )}
                          title={shared
                            ? 'Shared with your people leader — click to make private'
                            : 'Private to you — click to share with your people leader'}
                        >
                          {shared ? <Check className="h-3 w-3" /> : <Send className="h-3 w-3" />}
                          {shared ? 'Shared' : 'Share'}
                        </button>
                      )
                    })()}

                    {/* Carry-forward — only visible when the entry is the
                        right shape for it: fully-locked Mid-Year, owned by
                        the current user, and not already carried forward.
                        v1 only supports Mid-Year → Year-End in the same year. */}
                    {(() => {
                      if (!onCarryForwardEvidence) return null
                      if (evidenceItem.lockState !== 'fully_locked') return null
                      if (evidenceItem.reviewPeriod !== 'MIDYEAR') return null
                      if (evidenceItem.derivedToEvidenceId) return null
                      const effectiveUserId = currentUserId || targetUserId
                      const isOwner = effectiveUserId && targetUserId && targetUserId === effectiveUserId
                      if (!isOwner) return null
                      return (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCarryForwardSource(evidenceItem)}
                          title="Continue at Year-End — creates a new editable Year-End entry from this locked Mid-Year one."
                          className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                        >
                          <ArrowRight className="h-4 w-4 mr-1" />
                          <span className="text-xs">Continue at Year-End</span>
                        </Button>
                      )
                    })()}
                    {canEditEvidence(evidenceItem) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEdit(evidenceItem)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {canDeleteEvidence(evidenceItem) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(evidenceItem.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Attachments Section */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Paperclip className="h-4 w-4" />
                      Attachments {evidenceItem.attachments && evidenceItem.attachments.length > 0 && (
                        <Badge variant="outline" className="ml-1">{evidenceItem.attachments.length}</Badge>
                      )}
                    </h4>
                    {canEditEvidence(evidenceItem) && (
                      <>
                        <input
                          id={`file-input-${evidenceItem.id}`}
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.bmp,.txt,.csv,.zip,.rar"
                          onChange={async (e) => {
                            const file = e.target.files?.[0]
                            if (file && evidenceItem.id) {
                              try {
                                setUploadingAttachment(evidenceItem.id)
                                await performanceService.uploadAttachment(evidenceItem.id, file)
                                
                                // Refresh evidence to show new attachment
                                if (onRefreshEvidence) {
                                  await onRefreshEvidence()
                                }
                                
                                toast.success('Attachment uploaded successfully')
                              } catch (error) {
                                console.error('Upload failed:', error)
                                toast.error('Failed to upload attachment')
                              } finally {
                                setUploadingAttachment(null)
                                // Reset the input
                                const inputEl = document.getElementById(`file-input-${evidenceItem.id}`) as HTMLInputElement
                                if (inputEl) {
                                  inputEl.value = ''
                                }
                              }
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const inputEl = document.getElementById(`file-input-${evidenceItem.id}`) as HTMLInputElement
                            inputEl?.click()
                          }}
                          disabled={uploadingAttachment === evidenceItem.id}
                        >
                          <Upload className="h-3 w-3 mr-1" />
                          {uploadingAttachment === evidenceItem.id ? 'Uploading...' : 'Add Attachment'}
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {evidenceItem.attachments && evidenceItem.attachments.length > 0 ? (
                    <div className="space-y-2">
                      {evidenceItem.attachments.map((attachment: any) => (
                        <div
                          key={attachment.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <File className="h-5 w-5 text-gray-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{attachment.name}</p>
                              <p className="text-xs text-gray-500">
                                {(attachment.size / 1024).toFixed(1)} KB • {attachment.created_at ? new Date(attachment.created_at).toLocaleDateString() : 'No date'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {performanceService.canViewInline(attachment.type) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={async () => {
                                  try {
                                    const blobUrl = await performanceService.viewAttachmentInline(attachment.id)
                                    window.open(blobUrl, '_blank')
                                  } catch (error) {
                                    toast.error('Failed to open file')
                                  }
                                }}
                                title="Open in browser"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                            {performanceService.canViewWithOffice(attachment.type) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={async () => {
                                  try {
                                    const url = await performanceService.getOfficeViewerUrl(attachment.id)
                                    window.open(url, '_blank')
                                  } catch (error) {
                                    toast.error('Failed to open file in Office Online')
                                  }
                                }}
                                title="Open with Office Online"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                try {
                                  await performanceService.downloadAttachment(attachment.id, attachment.name)
                                } catch (error) {
                                  toast.error('Failed to download file')
                                }
                              }}
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {(() => {
                              // Determine which user ID to check for delete permission
                              const effectiveUserId = currentUserId || targetUserId
                              if (!effectiveUserId) return false
                              // Can delete if: user uploaded it OR user created the evidence
                              const canDelete = attachment.uploaded_by === effectiveUserId || evidenceItem.createdBy === effectiveUserId
                              return canEditEvidence(evidenceItem) && canDelete
                            })() && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setAttachmentToDelete({
                                    id: attachment.id,
                                    evidenceId: evidenceItem.id,
                                    name: attachment.name
                                  })
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No attachments</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )
        })()}
      </div>

      {/* Add/Edit Evidence Dialog */}
      <Dialog open={showEvidenceForm} onOpenChange={(open) => !open && resetForm(true)}>
        <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingEvidence ? 'Edit Entry' : 'Add New Entry'}
            </DialogTitle>
            <DialogDescription>
              {userRole === 'manager'
                ? 'Add a people leader entry or update an existing entry for this team member'
                : 'Link to a KPI to record measurements and update progress, or add a general impact & growth entry'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4 flex-1 min-h-0 overflow-y-auto">
            {/* Honest framing (task #4) for NON-measurement, team-member
                narratives: these stay private to you until you choose to
                share them with your people leader. Measurements carry their own
                "shared as KPI evidence" note inside the KPI form, so we
                suppress this line the moment a measurement value is present.
                PL-authored entries get no privacy promise here. */}
            {userRole !== 'manager' && !(evidenceForm.kpiId && measurementValue) && (
              <div className="flex items-start gap-1.5 rounded-md bg-stone-50 border border-stone-200 px-2.5 py-2 text-xs text-stone-600">
                <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  This entry stays private to you until you share it with your
                  people leader.
                </span>
              </div>
            )}

            {/* Observations mode — these are PL-private notes that never record
                a KPI measurement, so we suppress every KPI/measurement
                affordance below and explain why up front. */}
            {observationsMode && (
              <div className="flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2 text-xs text-amber-700">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Observations are private notes about this team member. They do
                  not record KPI measurements.
                </span>
              </div>
            )}

            {/* KPA Selector — only when the parent doesn't bind a KPA via
                the `kpaId` prop (i.e. top-level Portfolio surface). Per-KPA
                surfaces (MemberKPAPanel) already lock the KPA via the prop
                so we skip the redundant picker there. Leaving the selector
                on "General" produces an entry with kpaId=null — the
                "General category" requested by HR feedback for impact items
                that don't belong to any specific KPA. */}
            {kpaId === undefined && kpaOptions && kpaOptions.length > 0 && (() => {
              // A measurement MUST belong to a KPA (via its KPI), so in
              // measurement mode we drop the "General" option and require a KPA.
              const isMeasurementMode = addMode === 'measurement' || evidenceForm.type === 'measurement'
              return (
              <div>
                <Label htmlFor="kpaId">Link to a KPA {isMeasurementMode ? '(Required)' : '(Optional)'}</Label>
                <Select
                  value={kpaOptionFor(evidenceForm.kpaId)?.id ?? (evidenceForm.kpaId || 'general')}
                  onValueChange={(value: string) => {
                    setEvidenceForm({
                      ...evidenceForm,
                      kpaId: value === 'general' ? undefined : value,
                    })
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isMeasurementMode ? 'Select a KPA' : 'General — not tied to a specific KPA'} />
                  </SelectTrigger>
                  <SelectContent>
                    {!isMeasurementMode && (
                      <SelectItem value="general">General — not tied to a specific KPA</SelectItem>
                    )}
                    {kpaOptions.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {isMeasurementMode ? (
                    'A measurement belongs to a KPA — pick the one this KPI sits under, or pick the KPI below and we will set it for you.'
                  ) : observationsMode ? (
                    <>Pick a KPA if this observation relates to one. Leave on
                    <span className="font-medium"> General </span> for an observation about
                    the team member as a whole.</>
                  ) : (
                    <>Pick a KPA if this entry relates to one. Leave on
                    <span className="font-medium"> General </span> for impact items that
                    cut across KPAs or stand alone.</>
                  )}
                </p>
              </div>
              )
            })()}

            {/* KPI Selector. A4 (May 2026 rework): when a KPA has been
                picked (either via the parent prop or the form's own KPA
                selector above), narrow the KPI list to that KPA's KPIs only.
                Without a KPA selected, show the full cross-KPA list so the
                user isn't forced through a two-step dance to find a KPI. */}
            {!observationsMode && availableKPIs.length > 0 && (() => {
              const isKPILocked = !!(preSelectedKPIId || (editingEvidence && editingEvidence.kpiId))
              const lockedKPIId = preSelectedKPIId || editingEvidence?.kpiId
              const effectiveKpaId = kpaId !== undefined ? kpaId : evidenceForm.kpaId
              // When a KPA is selected, narrow to that KPA's KPIs. We keep
              // KPIs with no `kpaId` (back-compat callers) only when no KPA
              // is selected at all — otherwise they'd appear unscoped and
              // confuse the experience.
              const scopedKPIs = effectiveKpaId
                ? availableKPIs.filter((k) => k.kpaId && String(k.kpaId) === String(effectiveKpaId))
                : availableKPIs
              // Recording a measurement REQUIRES a KPI — so in that mode we drop
              // the "leave unlinked / general entry" affordance entirely.
              const isMeasurementMode = addMode === 'measurement' || evidenceForm.type === 'measurement'
              return (
              <div>
                <Label htmlFor="kpiId">
                  Link to KPI {isMeasurementMode ? '(Required)' : isKPILocked ? '(Locked)' : '(Optional)'}
                </Label>
                {isKPILocked ? (
                  // KPI is locked (pre-selected from context or editing existing KPI-linked evidence) — show read-only
                  <>
                    <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                      <Target className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-800">
                        {availableKPIs.find(k => k.id === lockedKPIId)?.name || lockedKPIId}
                      </span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <Target className="h-3 w-3" />
                      This entry will update the selected KPI's progress
                    </p>
                  </>
                ) : (
                  <>
                    <Select
                      value={evidenceForm.kpiId || 'none'}
                      onValueChange={(value: any) => {
                        const newKpiId = value === 'none' ? undefined : value
                        const pickedKpi = newKpiId ? availableKPIs.find(k => k.id === newKpiId) : undefined
                        setEvidenceForm({
                          ...evidenceForm,
                          kpiId: newKpiId,
                          // Auto-set to measurement type when KPI is selected
                          type: newKpiId ? 'measurement' : evidenceForm.type,
                          // When picking a KPI from the cross-KPA list (top-level
                          // surface, no KPA bound by the parent), sync the KPA
                          // selector above to the KPI's own KPA so the two stay
                          // consistent.
                          kpaId: (kpaId === undefined && pickedKpi?.kpaId) ? pickedKpi.kpaId : evidenceForm.kpaId,
                        })
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={
                          effectiveKpaId && scopedKPIs.length === 0
                            ? 'No KPIs under the selected KPA'
                            : isMeasurementMode ? 'Select a KPI' : 'Select KPI or leave unlinked'
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        {!isMeasurementMode && (
                          <SelectItem value="none">No specific KPI (General entry)</SelectItem>
                        )}
                        {scopedKPIs.map((kpi) => (
                          <SelectItem key={kpi.id} value={kpi.id}>
                            {kpi.name} {kpi.objectiveTitle && `(${kpi.objectiveTitle})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {evidenceForm.kpiId && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                        <Target className="h-3 w-3" />
                        This entry will update the selected KPI's progress
                      </p>
                    )}
                    {!evidenceForm.kpiId && (
                      <p className="text-xs text-gray-500 mt-1">
                        {isMeasurementMode
                          ? 'Choose the KPI this measurement updates.'
                          : effectiveKpaId
                            ? 'Pick a KPI from this KPA, or leave it unlinked for a KPA-level entry.'
                            : 'Link to a KPI to track measurements, or leave unlinked for a general entry.'}
                      </p>
                    )}
                  </>
                )}
              </div>
              )
            })()}

            {/* Entry Type / Entry Source — only when no KPI is selected. In
                Simple CREATE these are hidden behind the "Add more detail"
                disclosure (rework task #13); Detailed and Edit always show
                them. The defaults (type='achievement', source auto-derived)
                are sensible without the user touching anything. The
                user-facing Visibility control was removed — privacy is no
                longer a per-entry choice. */}
            {!observationsMode && !evidenceForm.kpiId && showAdvanced && (
            <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type">Entry Type</Label>
                {editingEvidence ? (
                  <div className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                    <span className="text-sm text-gray-700">
                      {{ measurement: 'KPI Measurement', achievement: 'Achievement', project_outcome: 'Project Outcome', stakeholder_feedback: 'Stakeholder Feedback', document: 'Document', link: 'External Link' }[evidenceForm.type!] || evidenceForm.type}
                    </span>
                  </div>
                ) : (
                  <Select
                    value={evidenceForm.type}
                    onValueChange={(value: any) => {
                      if (value === 'measurement' && !evidenceForm.kpiId) {
                        // Show a visual cue that KPI is required
                      }
                      setEvidenceForm({...evidenceForm, type: value})
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="measurement">KPI Measurement</SelectItem>
                      <SelectItem value="achievement">Achievement</SelectItem>
                      <SelectItem value="project_outcome">Project Outcome</SelectItem>
                      <SelectItem value="stakeholder_feedback">Stakeholder Feedback</SelectItem>
                      <SelectItem value="document">Document</SelectItem>
                      <SelectItem value="link">External Link</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              {/* Classification field removed — entries are not rated in
                  either view. Backend defaults the classification to 'full'
                  on create so the column stays populated for legacy compat. */}
            </div>

            {/* The user-facing Visibility control was removed — privacy is
                derived from source + measurement + release state, not chosen
                per-entry. Entry Source stays as a read-only indicator of who
                is authoring the entry. */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="source">Entry Source</Label>
                <div className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                  <User className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700">
                    {userRole === 'manager' ? 'People Leader Entry' : 'Team Member Entry'}
                  </span>
                </div>
              </div>
            </div>
            </>
            )}

            {/* KPI Measurement Form. Shown when a KPI is selected, OR when the
                user opened the dialog via "Record Measurement" (addMode) so the
                measurement surface is visible while they pick a KPI in-dialog.
                Never shown in observations mode. */}
            {!observationsMode && (evidenceForm.kpiId || addMode === 'measurement') && (
              <div className="border-2 border-emerald-200 rounded-lg p-4 bg-gradient-to-br from-emerald-50 to-teal-50">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="h-5 w-5 text-emerald-600" />
                  <h4 className="font-semibold text-emerald-900">KPI Measurement</h4>
                </div>

                {/* When the user chose "Record Measurement" but hasn't yet
                    picked a KPI, guide them to the selector above. Submission
                    stays blocked (isFormValid) until a KPI is linked. */}
                {addMode === 'measurement' && !evidenceForm.kpiId && (
                  <div className="flex items-start gap-1.5 rounded-md bg-blue-50 border border-blue-200 px-2.5 py-2 text-xs text-blue-700 mb-3">
                    <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>Select a KPI above to record a measurement.</span>
                  </div>
                )}

                {/* Honest copy (task #4): a KPI-linked entry that carries a
                    value is a measurement — it moves the KPI number, so it is
                    NOT private. It is shared with the people leader as the
                    evidence behind that number. We surface this the moment a
                    value is present so the framing matches the access model
                    (no "private to you" promise for measurements). */}
                {!!measurementValue && (
                  <div className="mb-3 flex items-start gap-1.5 rounded-md bg-emerald-100/60 border border-emerald-200 px-2.5 py-2 text-xs text-emerald-900">
                    <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      {userRole === 'manager'
                        ? `Measurements update ${ownerDisplayName || 'this team member'}'s KPI progress and are visible to both of you as the evidence behind the number.`
                        : 'Measurements update your KPI progress and are shared with your people leader as the evidence behind it.'}
                    </span>
                  </div>
                )}

                {/* Get selected KPI details */}
                {(() => {
                  const selectedKPI = availableKPIs.find(k => k.id === evidenceForm.kpiId)
                  // Infer measurement config for backward compatibility
                  const inferredMode = selectedKPI ? inferMeasurementMode(selectedKPI) : 'numeric'
                  const inferredDirection = selectedKPI ? inferMeasurementDirection(selectedKPI) : 'higher_is_better'
                  
                  return selectedKPI && (
                    <div className="space-y-3">
                      <div className="bg-white/60 rounded p-2 text-sm">
                        <p className="text-gray-700"><strong>KPI:</strong> {selectedKPI.name}</p>
                        {selectedKPI.objectiveTitle && (
                          <p className="text-gray-600 text-xs mt-1">Objective: {selectedKPI.objectiveTitle}</p>
                        )}
                      </div>
                      
                      {/* Measurement Direction Indicator */}
                      {inferredDirection === 'lower_is_better' && (
                        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
                          <Info className="h-4 w-4 text-blue-600" />
                          <span className="text-xs text-blue-700 font-medium">
                            Lower values indicate better impact & growth for this KPI
                          </span>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs text-gray-700">Current Value</Label>
                          {/* Show appropriate current value display based on mode */}
                          {inferredMode === 'rating' ? (
                            <div className={`mt-1 text-sm font-medium rounded px-3 py-2 ${
                              viewMode === 'detailed'
                                ? ((selectedKPI.current_value || 0) >= 100 ? 'bg-green-100 text-green-700' :
                                   (selectedKPI.current_value || 0) >= 50 ? 'bg-amber-100 text-amber-700' :
                                   (selectedKPI.current_value || 0) > 0 ? 'bg-red-100 text-red-700' :
                                   'bg-gray-100 text-gray-500')
                                // Simple view: calm stone surface, no traffic-light.
                                : 'bg-stone-100 text-stone-700'
                            }`}>
                              {(selectedKPI.current_value || 0) >= 100 ? 'On Track' :
                               (selectedKPI.current_value || 0) >= 50 ? 'Mostly on Track' :
                               (selectedKPI.current_value || 0) > 0 ? 'Not on Track' : 'Not Rated'}
                            </div>
                          ) : inferredMode === 'binary' ? (
                            <div className={`mt-1 text-sm font-medium rounded px-3 py-2 ${
                              viewMode === 'detailed'
                                ? ((selectedKPI.current_value || 0) >= 100 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')
                                // Simple view: emerald only for the genuine "Achieved" state.
                                : ((selectedKPI.current_value || 0) >= 100 ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-700')
                            }`}>
                              {(selectedKPI.current_value || 0) >= 100 ? 'Achieved' : 'Not Achieved'}
                            </div>
                          ) : (
                            <Input
                              disabled
                              value={selectedKPI.current_value != null 
                                ? `${selectedKPI.current_value}${selectedKPI.unit ? ` ${selectedKPI.unit}` : ''}`
                                : '--'
                              } 
                              className="mt-1 bg-gray-100 text-gray-600"
                            />
                          )}
                        </div>
                        <div>
                          {/* Per-period measurement lock. When the period
                              currently being viewed is captured in a
                              finalised check-in, the value is read-only;
                              the OTHER period's value remains editable on
                              dual-period entries. */}
                          {(() => {
                            const ls = editingEvidence?.lockState
                            const periodLocked = (
                              ls === 'fully_locked' ||
                              (ls === 'midyear_locked' && reviewPeriod === 'MIDYEAR') ||
                              (ls === 'yearend_locked' && reviewPeriod === 'YEAREND')
                            )
                            if (!periodLocked) return null
                            const periodLabel = reviewPeriod === 'YEAREND' ? 'Year-End' : 'Mid-Year'
                            return (
                              <div className="mb-2 px-2 py-1.5 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-900 flex items-start gap-1.5">
                                <Lock className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                <span>
                                  The {periodLabel} value is captured in the historical record and cannot be changed.
                                  {ls && ls !== 'fully_locked' && ' The other period is still editable.'}
                                </span>
                              </div>
                            )
                          })()}
                          <Label className="text-xs text-gray-700">
                            New Value *
                            {inferredDirection === 'lower_is_better' && (
                              <span className="text-blue-600 ml-1">↓</span>
                            )}
                          </Label>

                          {/* Rating Mode: Dropdown with status options */}
                          {inferredMode === 'rating' ? (
                            <Select
                              value={measurementValue}
                              onValueChange={(value) => setMeasurementValue(value)}
                              disabled={(() => {
                                const ls = editingEvidence?.lockState
                                return (
                                  ls === 'fully_locked' ||
                                  (ls === 'midyear_locked' && reviewPeriod === 'MIDYEAR') ||
                                  (ls === 'yearend_locked' && reviewPeriod === 'YEAREND')
                                )
                              })()}
                            >
                              <SelectTrigger className="mt-1 font-semibold">
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                              <SelectContent>
                                {(selectedKPI.rating_options || [
                                  { label: 'Not on Track', value: 0, color: '#EF4444' },
                                  { label: 'Mostly on Track', value: 50, color: '#F59E0B' },
                                  { label: 'On Track', value: 100, color: '#10B981' }
                                ]).map((option) => (
                                  <SelectItem key={option.value} value={String(option.value)}>
                                    <div className="flex items-center gap-2">
                                      <div 
                                        className="w-3 h-3 rounded-full" 
                                        style={{ backgroundColor: option.color }}
                                      />
                                      {option.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : inferredMode === 'binary' ? (
                            /* Binary Mode: Achieved/Not Achieved toggle */
                            <Select
                              value={measurementValue}
                              onValueChange={(value) => setMeasurementValue(value)}
                              disabled={(() => {
                                const ls = editingEvidence?.lockState
                                return (
                                  ls === 'fully_locked' ||
                                  (ls === 'midyear_locked' && reviewPeriod === 'MIDYEAR') ||
                                  (ls === 'yearend_locked' && reviewPeriod === 'YEAREND')
                                )
                              })()}
                            >
                              <SelectTrigger className="mt-1 font-semibold">
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="100">
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-green-500" />
                                    Achieved
                                  </div>
                                </SelectItem>
                                <SelectItem value="0">
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-red-500" />
                                    Not Achieved
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            /* Numeric/Percentage Mode: Input with validation */
                            <Input
                              placeholder={`Enter value${selectedKPI.unit ? ` (${selectedKPI.unit})` : ''}${
                                inferredMode === 'percentage' ? ' 0-100' : ''
                              }`}
                              className="mt-1 font-semibold"
                              type="number"
                              step="0.1"
                              min={selectedKPI.min_value ?? (inferredMode === 'percentage' ? 0 : undefined)}
                              max={selectedKPI.max_value ?? (inferredMode === 'percentage' ? 100 : undefined)}
                              disabled={(() => {
                                const ls = editingEvidence?.lockState
                                return (
                                  ls === 'fully_locked' ||
                                  (ls === 'midyear_locked' && reviewPeriod === 'MIDYEAR') ||
                                  (ls === 'yearend_locked' && reviewPeriod === 'YEAREND')
                                )
                              })()}
                              value={measurementValue}
                              onChange={(e) => {
                                const value = e.target.value
                                // Allow empty, numbers, and decimal point
                                if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                                  // Validate percentage mode bounds
                                  if (inferredMode === 'percentage') {
                                    const numVal = parseFloat(value)
                                    if (!isNaN(numVal) && (numVal < 0 || numVal > 100)) {
                                      return // Don't update if outside percentage bounds
                                    }
                                  }
                                  setMeasurementValue(value)
                                }
                              }}
                              onKeyPress={(e) => {
                                // Prevent non-numeric characters except minus and decimal
                                if (!/[0-9.-]/.test(e.key)) {
                                  e.preventDefault()
                                }
                              }}
                            />
                          )}
                          
                          {/* Validation hint for percentage mode */}
                          {inferredMode === 'percentage' && measurementValue && (
                            parseFloat(measurementValue) < 0 || parseFloat(measurementValue) > 100
                          ) && (
                            <p className="text-xs text-red-600 mt-1">Value must be between 0 and 100%</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs text-gray-700">Target</Label>
                          {/* Show appropriate target display based on mode */}
                          {inferredMode === 'rating' ? (
                            <div className="mt-1 text-sm text-gray-600 bg-gray-100 rounded px-3 py-2">
                              On Track
                            </div>
                          ) : inferredMode === 'binary' ? (
                            <div className="mt-1 text-sm text-gray-600 bg-gray-100 rounded px-3 py-2">
                              Achieved
                            </div>
                          ) : (
                            <Input
                              disabled
                              value={selectedKPI.target_value != null 
                                ? `${selectedKPI.target_value}${selectedKPI.unit ? ` ${selectedKPI.unit}` : ''}${
                                    inferredDirection === 'lower_is_better' ? ' ↓' : ''
                                  }`
                                : '--'
                              }
                              className="mt-1 bg-gray-100 text-gray-600"
                            />
                          )}
                        </div>
                      </div>

                      {/* File attachment for measurement evidence */}
                      <div className="border-2 border-dashed border-emerald-300 rounded p-3 bg-white/40">
                        <Label htmlFor="measurement-file" className="flex items-center gap-2 text-sm">
                          <Upload className="h-4 w-4" />
                          Attach Supporting Document (Optional)
                        </Label>
                        <Input
                          id="measurement-file"
                          type="file"
                          className="mt-2"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) {
                              setSelectedFile(file)
                            }
                          }}
                        />
                        {selectedFile && (
                          <p className="text-xs text-gray-600 mt-2 flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                          </p>
                        )}
                      </div>

                      {/* Classification field intentionally removed from
                          both Simple and Detailed forms. */}
                    </div>
                  )
                })()}
              </div>
            )}

            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={evidenceForm.title || ''}
                onChange={(e) => setEvidenceForm({...evidenceForm, title: e.target.value})}
                placeholder="Enter entry title"
              />
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={evidenceForm.description || ''}
                onChange={(e) => setEvidenceForm({...evidenceForm, description: e.target.value})}
                placeholder="Describe the entry in detail"
                rows={3}
              />
            </div>

            {/* General attachment — surfaced for the simple, KPI-less entry
                so the 4-field Simple create form still offers "Attachment"
                (rework task #13). The KPI form has its own attach control,
                and the Document type has a required one, so we only show
                this calm optional field when neither applies. The selected
                file is uploaded as an attachment after the entry is created
                (see handleAddEvidence). */}
            {!evidenceForm.kpiId && evidenceForm.type !== 'document' && (
              <div>
                <Label htmlFor="entry-attachment" className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Attachment (Optional)
                </Label>
                <Input
                  id="entry-attachment"
                  type="file"
                  className="mt-1"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.bmp,.txt,.csv,.zip,.rar"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                />
                {selectedFile && (
                  <p className="text-xs text-gray-600 mt-1.5 flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            )}

            {/* Phase 1: dual-period opt-in. "Relevant to both Mid-Year and
                Year-End" is a RELEASE-time decision, not a create-time one
                (rework task #13) — at create we default to the current
                period (`reviewPeriod`). So this checkbox only appears when
                EDITING an existing entry, never on a fresh add. */}
            {editingEvidence && (() => {
              // Measurements carry forward automatically (Mid-Year → Year-End
              // for continuing objectives), so the dual-period opt-in is
              // irrelevant and confusing on them — hide it. It only applies to
              // narrative entries.
              const isMeasurement = (
                (!!evidenceForm.kpiId && !!measurementValue) ||
                (!!editingEvidence.kpiId && (
                  editingEvidence.measurementValue != null ||
                  editingEvidence.midyearMeasurementValue != null ||
                  editingEvidence.yearendMeasurementValue != null
                ))
              )
              if (isMeasurement) return null

              // Once one half is locked, retracting the dual flag would
              // erase this entry from the live half's view AND drop a row
              // that the locked half's historical record relies on. The
              // checkbox stays visible so the user understands the state,
              // but it can't be unticked.
              const partiallyLocked = (
                editingEvidence?.lockState === 'midyear_locked' ||
                editingEvidence?.lockState === 'yearend_locked' ||
                editingEvidence?.lockState === 'fully_locked'
              )
              // The period this entry was originally captured for drives the
              // "only shows up in your X view" copy — NOT the current view.
              // Falls back to the current view period for legacy entries that
              // have no stored capture period.
              const capturedLabel =
                (editingEvidence.reviewPeriod || reviewPeriod) === 'MIDYEAR' ? 'Mid-Year' : 'Year-End'
              return (
                <div className="flex items-start gap-3 rounded-md border border-stone-200 bg-stone-50 p-3">
                  <input
                    id="applies-to-both-periods"
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-stone-300 text-heritage-blue focus:ring-heritage-blue disabled:opacity-60"
                    checked={!!evidenceForm.appliesToBothPeriods}
                    disabled={partiallyLocked && !!evidenceForm.appliesToBothPeriods}
                    onChange={(e) => setEvidenceForm({ ...evidenceForm, appliesToBothPeriods: e.target.checked })}
                  />
                  <div className="flex-1">
                    <Label htmlFor="applies-to-both-periods" className="text-sm font-medium cursor-pointer">
                      Relevant to both Mid-Year and Year-End
                    </Label>
                    <p className="text-xs text-stone-600 mt-0.5">
                      By default this entry only shows up in your{' '}
                      <span className="font-medium">{capturedLabel}</span>{' '}
                      view. Tick this if it's an achievement you'll want to discuss in both check-ins.
                    </p>
                    {partiallyLocked && evidenceForm.appliesToBothPeriods && (
                      <p className="text-xs text-blue-700 mt-1.5">
                        Locked — one period is already in the historical record, so this can't be retracted.
                      </p>
                    )}
                  </div>
                </div>
              )
            })()}

            {/* Type-Specific Fields - Only show when no KPI is selected */}
            {!evidenceForm.kpiId && evidenceForm.type === 'document' && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                <Label htmlFor="file-upload" className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Attach Document *
                </Label>
                <Input
                  id="file-upload"
                  type="file"
                  className="mt-2"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      setSelectedFile(file)
                      setEvidenceForm({...evidenceForm, title: evidenceForm.title || file.name})
                    }
                  }}
                />
                {selectedFile && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            )}

            {!evidenceForm.kpiId && evidenceForm.type === 'link' && (
              <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <Label htmlFor="external-link" className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  External Link *
                </Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="URL"
                    value={evidenceForm.externalLink?.url || ''}
                    onChange={(e) => setEvidenceForm({
                      ...evidenceForm, 
                      externalLink: {
                        ...evidenceForm.externalLink,
                        url: e.target.value,
                        title: evidenceForm.externalLink?.title || ''
                      }
                    })}
                  />
                  <Input
                    placeholder="Link Title"
                    value={evidenceForm.externalLink?.title || ''}
                    onChange={(e) => setEvidenceForm({
                      ...evidenceForm, 
                      externalLink: {
                        ...evidenceForm.externalLink,
                        url: evidenceForm.externalLink?.url || '',
                        title: e.target.value
                      }
                    })}
                  />
                </div>
              </div>
            )}

            {!evidenceForm.kpiId && evidenceForm.type === 'measurement' && (
              <div className="border border-emerald-200 rounded-lg p-4 bg-emerald-50">
                <Label className="flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4" />
                  Measurement Details
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <Label htmlFor="metric-name" className="text-xs">Metric Name</Label>
                    <Input
                      id="metric-name"
                      placeholder="e.g., Pass Rate"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="metric-value" className="text-xs">Value</Label>
                    <Input
                      id="metric-value"
                      placeholder="e.g., 85%"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="metric-target" className="text-xs">Target</Label>
                    <Input
                      id="metric-target"
                      placeholder="e.g., 80%"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            )}

            {!evidenceForm.kpiId && evidenceForm.type === 'stakeholder_feedback' && (
              <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                <Label htmlFor="feedback-source" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Feedback Source *
                </Label>
                <Input
                  id="feedback-source"
                  placeholder="e.g., Dr. Smith (Head of Department)"
                  className="mt-2"
                />
                <Label htmlFor="feedback-date" className="flex items-center gap-2 mt-3">
                  <Calendar className="h-4 w-4" />
                  Feedback Date
                </Label>
                <Input
                  id="feedback-date"
                  type="date"
                  className="mt-2"
                />
              </div>
            )}

            {!evidenceForm.kpiId && evidenceForm.type === 'project_outcome' && (
              <div className="border border-amber-200 rounded-lg p-4 bg-amber-50">
                <Label htmlFor="project-name" className="text-sm font-medium">Project Name</Label>
                <Input
                  id="project-name"
                  placeholder="Enter project name"
                  className="mt-2"
                />
                <Label htmlFor="completion-date" className="text-sm font-medium mt-3 block">Completion Date</Label>
                <Input
                  id="completion-date"
                  type="date"
                  className="mt-2"
                />
              </div>
            )}

            {/* Collapsible Additional Fields. In Simple CREATE this is the
                single "Add more detail" disclosure that also reveals the
                Entry Type / Visibility / Entry Source block above (rework
                task #13). In Detailed / Edit it's the usual "Additional
                Details" expander for impact / supporting detail / link. */}
            <div className="border-t border-gray-200 pt-4">
              <div
                onClick={() => setShowAdditionalFields(!showAdditionalFields)}
                className="flex items-center justify-between cursor-pointer group"
              >
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer group-hover:text-gray-700">
                  {simpleCreate ? 'Add more detail' : 'Additional Details (Optional)'}
                </label>
                <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform group-hover:text-gray-700 ${showAdditionalFields ? 'rotate-180' : ''}`} />
              </div>

              {showAdditionalFields && (
                <div className="mt-4 space-y-4 animate-in slide-in-from-top-2">
                  <div>
                    <Label htmlFor="impact">Impact</Label>
                    <Textarea
                      id="impact"
                      value={evidenceForm.impact || ''}
                      onChange={(e) => setEvidenceForm({...evidenceForm, impact: e.target.value})}
                      placeholder="What was the impact or outcome?"
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label htmlFor="evidence">Supporting Detail</Label>
                    <Textarea
                      id="evidence"
                      value={evidenceForm.evidence || ''}
                      onChange={(e) => setEvidenceForm({...evidenceForm, evidence: e.target.value})}
                      placeholder="Provide supporting details, metrics, or references"
                      rows={2}
                    />
                  </div>

                  {/* Show external link section for non-link types as optional */}
                  {evidenceForm.type !== 'link' && (
                    <div>
                      <Label htmlFor="external-link">External Link (Optional)</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="URL"
                          value={evidenceForm.externalLink?.url || ''}
                          onChange={(e) => setEvidenceForm({
                            ...evidenceForm, 
                            externalLink: {
                              ...evidenceForm.externalLink,
                              url: e.target.value,
                              title: evidenceForm.externalLink?.title || ''
                            }
                          })}
                        />
                        <Input
                          placeholder="Link Title"
                          value={evidenceForm.externalLink?.title || ''}
                          onChange={(e) => setEvidenceForm({
                            ...evidenceForm, 
                            externalLink: {
                              ...evidenceForm.externalLink,
                              url: evidenceForm.externalLink?.url || '',
                              title: e.target.value
                            }
                          })}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => resetForm(true)}>Cancel</Button>
            <Button 
              onClick={editingEvidence ? handleUpdateEvidence : handleAddEvidence}
              disabled={!isFormValid() || savingEntry}
            >
              <Save className="h-4 w-4 mr-2" />
              {editingEvidence ? 'Update Entry' : (savingEntry ? 'Saving…' : 'Add Entry')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={showDocumentUpload} onOpenChange={setShowDocumentUpload}>
        <DialogContent className="w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a document and provide context for this entry
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div>
              <Label htmlFor="file">Select File</Label>
              <Input
                id="file"
                type="file"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
              />
            </div>
            
            <div>
              <Label htmlFor="doc-title">Document Title *</Label>
              <Input
                id="doc-title"
                value={evidenceForm.title || ''}
                onChange={(e) => setEvidenceForm({...evidenceForm, title: e.target.value})}
                placeholder="Enter document title"
              />
            </div>
            
            <div>
              <Label htmlFor="doc-description">Description *</Label>
              <Textarea
                id="doc-description"
                value={evidenceForm.description || ''}
                onChange={(e) => setEvidenceForm({...evidenceForm, description: e.target.value})}
                placeholder="Describe what this document demonstrates"
                rows={3}
              />
            </div>

            {/* Phase 1: dual-period opt-in mirrored on the upload-document
                form so document-type entries can also span both check-ins. */}
            <div className="flex items-start gap-3 rounded-md border border-stone-200 bg-stone-50 p-3">
              <input
                id="doc-applies-to-both-periods"
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-stone-300 text-heritage-blue focus:ring-heritage-blue"
                checked={!!evidenceForm.appliesToBothPeriods}
                onChange={(e) => setEvidenceForm({ ...evidenceForm, appliesToBothPeriods: e.target.checked })}
              />
              <div className="flex-1">
                <Label htmlFor="doc-applies-to-both-periods" className="text-sm font-medium cursor-pointer">
                  Relevant to both Mid-Year and Year-End
                </Label>
                <p className="text-xs text-stone-600 mt-0.5">
                  Tick this if the document supports both check-in conversations.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentUpload(false)}>Cancel</Button>
            <Button
              onClick={handleDocumentUpload}
              disabled={!selectedFile || !evidenceForm.title || !evidenceForm.description}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload to Impact Portfolio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Carry-Forward Confirmation Dialog */}
      <AlertDialog open={!!carryForwardSource} onOpenChange={(open) => !open && !carryingForward && setCarryForwardSource(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Continue at Year-End?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new <span className="font-semibold">Year-End</span> entry pre-filled
              with this entry's title, description{carryForwardSource?.kpiId ? ', KPI link' : ''},
              and a reference copy of any attachments. The original Mid-Year entry stays as the
              historical record — it won't be edited.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {carryForwardSource && (
            <div className="rounded-md border border-stone-200 bg-stone-50 p-3 my-2 text-sm">
              <div className="font-medium text-stone-900">{carryForwardSource.title}</div>
              {carryForwardSource.description && (
                <div className="text-stone-600 text-xs mt-1 line-clamp-2">
                  {carryForwardSource.description}
                </div>
              )}
              <div className="text-xs text-stone-500 mt-1.5 flex items-center gap-2">
                <Calendar className="h-3 w-3" />
                {new Date(carryForwardSource.date).toLocaleDateString()}
                {carryForwardSource.kpiId && (
                  <>
                    <span>•</span>
                    <span>Linked to a KPI</span>
                  </>
                )}
              </div>
            </div>
          )}
          <p className="text-xs text-stone-600">
            If the linked KPI has been retired from your role since Mid-Year, the new entry will be
            created as general impact portfolio (no KPI link) and you'll see a notice.
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={carryingForward}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCarryForward}
              disabled={carryingForward}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              {carryingForward ? 'Creating…' : 'Continue at Year-End'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Impact Portfolio Entry Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Impact Portfolio Entry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this impact portfolio entry? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Attachment Confirmation Dialog */}
      <AlertDialog open={!!attachmentToDelete} onOpenChange={(open) => !open && setAttachmentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Attachment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{attachmentToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                if (!attachmentToDelete) return
                
                try {
                  await performanceService.deleteAttachment(attachmentToDelete.id)
                  
                  // Refresh evidence to show updated list
                  if (onRefreshEvidence) {
                    await onRefreshEvidence()
                  }
                  
                  toast.success('Attachment deleted')
                } catch (error) {
                  console.error('Delete failed:', error)
                  toast.error('Failed to delete attachment')
                } finally {
                  setAttachmentToDelete(null)
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
